/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Truck,
  Eye,
  Plus,
  Search,
  FileText,
  Camera,
  Download,
  FileDown,
  ChevronRight,
  X,
  Building2,
  User,
  DollarSign,
  Layers,
  PieChart as PieIcon,
  BarChart2,
  ShieldAlert,
  Package,
  Calendar,
  AlertOctagon,
  Undo2,
  Trash2,
  Upload,
  Image as ImageIcon,
  Check,
  History,
  FolderClock,
  Filter,
  Receipt,
  Pencil,
  Save
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  CartesianGrid
} from 'recharts';
import { Folio, Client, CostCenter, GoodsItem, ProcessStage, StageHistory, isClientUser } from '../types.ts';
import { downloadReworkIncidentPDF, downloadReworksAndReturnsStrategicReportPDF, ReworkIncidentForPDF } from '../utils/pdfGenerator.ts';
import { EditReworkIncidentModal } from './EditReworkIncidentModal';
import { dataService } from '../lib/dataService.ts';

export type IncidentType = 'RETRABAJO' | 'DEVOLUCION' | 'SCRAP';
export type IncidentOrigin = 'INSPECCION' | 'ENTREGA_PLANTA' | 'SCRAP_ITEM_DANADO';
export type IncidentStatus = 'PENDIENTE' | 'EN_REPROCESO' | 'REINSPECCIONADO' | 'RESUELTO' | 'DESECHADO';
export type IncidentPriority = 'BAJA' | 'MEDIA' | 'ALTA' | 'CRITICA';
export type ResponsibleDept = 'CALIDAD' | 'DECAPADO_PINTURA' | 'LOGISTICA_TRANSPORTE' | 'PRODUCCION' | 'CLIENTE';

export interface ReworkIncident {
  id: string; // INC-2026-001 or Folio ID
  folioId: string;
  clientId: string;
  clientName: string;
  costCenterId: string;
  costCenterName: string;
  type: IncidentType;
  originStage: IncidentOrigin;
  causeCategory: string; // e.g. "Mala Adherencia", "Decapado Incompleto", "Oxidación", "Golpe en Transporte", "Especificación Incorrecta", "Rechazo Cliente"
  causeDetail: string;
  affectedPieces: number;
  reworkPiecesName?: string; // Nombre / Descripción de las piezas a retrabajar
  costEstimateMXN: number;
  responsibleDept: ResponsibleDept;
  priority: IncidentPriority;
  status: IncidentStatus;
  reportedBy: string;
  dateReported: string; // ISO or YYYY-MM-DD
  actionPlan?: string;
  resolutionNotes?: string;
  photoUrl?: string;
  photos?: string[]; // Array of up to 7 image data URLs
}

interface ReworkAndReturnsManagerProps {
  folios: Folio[];
  clients: Client[];
  costCenters: CostCenter[];
  goods?: GoodsItem[];
  currentUser?: { name: string; role: string; clientCode?: string } | null;
  activeClient?: Client | null;
  selectedCostCenter?: CostCenter | null;
  onUpdateFolio: (folio: Folio) => void;
  onCreateFolio?: (folio: Folio) => void;
  isOnline?: boolean;
}

const COLORS = {
  retrabajo: '#f43f5e', // rose-500
  devolucion: '#ef4444', // red-500
  inspeccion: '#8b5cf6', // violet-500
  entregaPlanta: '#10b981', // emerald-500
  pendiente: '#f59e0b', // amber-500
  enReproceso: '#3b82f6', // blue-500
  resuelto: '#10b981', // emerald-500
  desechado: '#6b7280' // gray-500
};

const PHOTO_SLOT_LABELS = [
  'Foto #1 (Evidencia Principal)',
  'Foto #2 (Evidencia Defecto)',
  'Foto #3 (Evidencia Adicional)',
  'Foto #4 (Evidencia Adicional)',
  'Foto #5 (Evidencia Adicional)',
  'Foto #6 (Evidencia Adicional)',
  'Foto #7 (Evidencia Adicional)'
];

const SAMPLE_INCIDENTS: ReworkIncident[] = [
  {
    id: 'INC-2026-001',
    folioId: 'F-100234',
    clientId: 'c1',
    clientName: 'Metalsa SLP',
    costCenterId: 'cc1',
    costCenterName: 'Planta Chasis Bajío',
    type: 'RETRABAJO',
    originStage: 'INSPECCION',
    causeCategory: 'Decapado Incompleto',
    causeDetail: 'Residuos de cera e imprimante térmico en uniones de soldadura posterior.',
    affectedPieces: 24,
    costEstimateMXN: 4800,
    responsibleDept: 'DECAPADO_PINTURA',
    priority: 'ALTA',
    status: 'EN_REPROCESO',
    reportedBy: 'Ing. Carlos Mendoza (Calidad)',
    dateReported: '2026-08-04T10:30:00Z',
    actionPlan: 'Reingresar a tina de decapado químico alcalino por 20 min adicionales.',
    photos: []
  },
  {
    id: 'INC-2026-002',
    folioId: 'F-100239',
    clientId: 'c2',
    clientName: 'Nemak Monterrey',
    costCenterId: 'cc3',
    costCenterName: 'Planta Fundición 2',
    type: 'DEVOLUCION',
    originStage: 'ENTREGA_PLANTA',
    causeCategory: 'Rechazo Cliente',
    causeDetail: 'Rechazo parcial en rampa 4 por diferencia de espesor de recubrimiento fuera de micraje especificado (120 micras solicitadas vs 85 detectadas).',
    affectedPieces: 15,
    costEstimateMXN: 8500,
    responsibleDept: 'CLIENTE',
    priority: 'CRITICA',
    status: 'PENDIENTE',
    reportedBy: 'Chofer Manuel R. (Transporte)',
    dateReported: '2026-08-05T14:15:00Z',
    actionPlan: 'Retorno a CEDIS para recubrimiento secundario bajo norma ISO.',
    photos: []
  },
  {
    id: 'INC-2026-003',
    folioId: 'F-100241',
    clientId: 'c1',
    clientName: 'Metalsa SLP',
    costCenterId: 'cc1',
    costCenterName: 'Planta Chasis Bajío',
    type: 'RETRABAJO',
    originStage: 'ENTREGA_PLANTA',
    causeCategory: 'Golpe en Transporte',
    causeDetail: 'Abolladura ligera en arista de contenedor durante estiba y maniobra en rampa de entrega.',
    affectedPieces: 8,
    costEstimateMXN: 2200,
    responsibleDept: 'LOGISTICA_TRANSPORTE',
    priority: 'MEDIA',
    status: 'RESUELTO',
    reportedBy: 'Superv. Pedro Alarcón',
    dateReported: '2026-08-02T09:00:00Z',
    actionPlan: 'Enderezado manual y retocado localized spray en taller de sitio.',
    resolutionNotes: 'Aceptado por el cliente tras reinspección de calidad.',
    photos: []
  },
  {
    id: 'INC-BMW-001',
    folioId: '19265316',
    clientId: 'CLI-BMW',
    clientName: 'BMW GROUP',
    costCenterId: 'CC-BMW-SLP',
    costCenterName: 'Sede San Luis Potosí - Planta BMW',
    type: 'DEVOLUCION',
    originStage: 'ENTREGA_PLANTA',
    causeCategory: 'Rechazo Cliente',
    causeDetail: 'Rechazo parcial en rampa: 4 piezas con desviación dimensional en barrenos.',
    affectedPieces: 4,
    costEstimateMXN: 5200,
    responsibleDept: 'CLIENTE',
    priority: 'ALTA',
    status: 'RESUELTO',
    reportedBy: 'Inspector Calidad Planta BMW',
    dateReported: '2026-06-14T11:00:00Z',
    actionPlan: 'Inspección CMM y recalibración de sujeciones.',
    photos: []
  },
  {
    id: 'INC-BMW-002',
    folioId: '19265335',
    clientId: 'CLI-BMW',
    clientName: 'BMW GROUP',
    costCenterId: 'CC-BMW-SLP',
    costCenterName: 'Sede San Luis Potosí - Planta BMW',
    type: 'DEVOLUCION',
    originStage: 'ENTREGA_PLANTA',
    causeCategory: 'Rechazo Cliente',
    causeDetail: 'Rechazo en recepción SLP: 5 piezas con exceso de rugosidad superficial.',
    affectedPieces: 5,
    costEstimateMXN: 6800,
    responsibleDept: 'CLIENTE',
    priority: 'ALTA',
    status: 'RESUELTO',
    reportedBy: 'Chofer SLP - Unidad 04',
    dateReported: '2026-08-11T15:30:00Z',
    actionPlan: 'Ajuste de granulometría y granallado correctivo.',
    photos: []
  },
  {
    id: 'INC-BMW-003',
    folioId: '19284958',
    clientId: 'CLI-BMW',
    clientName: 'BMW GROUP',
    costCenterId: 'CC-BMW-PUE',
    costCenterName: 'Sede Puebla - Centro de Distribución BMW',
    type: 'DEVOLUCION',
    originStage: 'ENTREGA_PLANTA',
    causeCategory: 'Rechazo Cliente',
    causeDetail: 'Devolución parcial de 2 piezas por rebaba en arista lateral.',
    affectedPieces: 2,
    costEstimateMXN: 2400,
    responsibleDept: 'CLIENTE',
    priority: 'MEDIA',
    status: 'RESUELTO',
    reportedBy: 'Calidad BMW Puebla',
    dateReported: '2026-09-11T12:00:00Z',
    actionPlan: 'Desbaste fino manual y verificación de tolerancia.',
    photos: []
  }
];

export default function ReworkAndReturnsManager({
  folios,
  clients,
  costCenters,
  goods = [],
  currentUser,
  activeClient,
  selectedCostCenter,
  onUpdateFolio,
  onCreateFolio,
  isOnline = true
}: ReworkAndReturnsManagerProps) {
  const isUserClient = isClientUser(currentUser as any);

  // Current client scope resolve
  const currentScopedClient = useMemo(() => {
    if (activeClient) return activeClient;
    if (clients.length === 1) return clients[0];
    return null;
  }, [activeClient, clients]);

  // Current available cost centers filtered for current client
  const availableCostCenters = useMemo(() => {
    if (currentScopedClient) {
      const clientCCs = costCenters.filter(cc => cc.clientId === currentScopedClient.id);
      if (clientCCs.length > 0) return clientCCs;
    }
    return costCenters;
  }, [costCenters, currentScopedClient]);

  // Current default cost center
  const currentDefaultCostCenter = useMemo(() => {
    if (selectedCostCenter) return selectedCostCenter;
    if (availableCostCenters.length > 0) return availableCostCenters[0];
    return costCenters[0] || null;
  }, [selectedCostCenter, availableCostCenters, costCenters]);

  // Local incidents state - strictly exclude test mock data
  const [incidents, setIncidents] = useState<ReworkIncident[]>(() => {
    try {
      const saved = localStorage.getItem('sic_pro_rework_incidents');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          // Filter out any legacy dummy sample incidents
          return parsed.filter(inc => !inc.id?.startsWith('INC-2026-') && !inc.id?.startsWith('INC-BMW-'));
        }
      }
    } catch (err) {
      console.warn('Error reading saved incidents:', err);
    }
    return [];
  });

  // Sync to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('sic_pro_rework_incidents', JSON.stringify(incidents));
    } catch (err) {
      console.warn('Error saving incidents:', err);
    }
  }, [incidents]);

  // Real-time synchronization listener for uploaded reworks or scrap
  useEffect(() => {
    // Initial load from dataService / Firestore
    dataService.getIncidents().then(items => {
      if (items && items.length > 0) {
        setIncidents(items);
      }
    }).catch(err => console.warn('Could not load incidents from dataService:', err));

    const unsubFirestore = dataService.subscribeToIncidents((items) => {
      setIncidents(items);
    });

    const handleReload = () => {
      try {
        const saved = localStorage.getItem('sic_pro_rework_incidents');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            setIncidents(parsed.filter(inc => !inc.id?.startsWith('INC-2026-') && !inc.id?.startsWith('INC-BMW-')));
          }
        }
      } catch (e) {
        console.warn('Error reloading incidents:', e);
      }
    };

    window.addEventListener('rework_incidents_updated', handleReload);
    window.addEventListener('storage', handleReload);
    return () => {
      unsubFirestore();
      window.removeEventListener('rework_incidents_updated', handleReload);
      window.removeEventListener('storage', handleReload);
    };
  }, []);

  // Combine real Folios that have RETRABAJO or DEVOLUCION in status or history with incidents
  const combinedIncidents = useMemo(() => {
    let list: ReworkIncident[] = [...incidents];

    // Filter incidents by active client if in client context
    if (currentScopedClient) {
      list = list.filter(inc => 
        inc.clientId === currentScopedClient.id || 
        inc.clientName.toLowerCase().includes(currentScopedClient.name.toLowerCase())
      );
    }

    const existingFolioIds = new Set(list.map(inc => inc.folioId));

    folios.forEach(folio => {
      // Check if folio belongs to active client
      if (currentScopedClient && folio.clientId !== currentScopedClient.id && folio.clientName !== currentScopedClient.name) {
        return;
      }

      const hasReworkStatus = folio.status === 'RETRABAJO';
      const hasDevolucionStatus = folio.status === 'DEVOLUCION';
      const historyRework = folio.history?.find(h => h.stage === 'RETRABAJO');
      const historyDevolucion = folio.history?.find(h => h.stage === 'DEVOLUCION');

      if ((hasReworkStatus || hasDevolucionStatus || historyRework || historyDevolucion) && !existingFolioIds.has(folio.id)) {
        const isDevolucion = hasDevolucionStatus || !!historyDevolucion;
        const stageEntry = isDevolucion ? historyDevolucion : historyRework;
        
        let originStage: IncidentOrigin = 'INSPECCION';
        if (isDevolucion || folio.status === 'ENTREGA_PLANTA' || stageEntry?.stage === 'DEVOLUCION') {
          originStage = 'ENTREGA_PLANTA';
        }

        let totalPieces = 0;
        if (stageEntry?.returnedItems && stageEntry.returnedItems.length > 0) {
          totalPieces = stageEntry.returnedItems.reduce((acc, curr) => acc + (Number(curr.quantityReturned) || 0), 0);
        } else {
          totalPieces = folio.items?.reduce((acc, curr) => acc + (Number(curr.quantity) || 0), 0) || 10;
        }

        list.push({
          id: `INC-${folio.id}`,
          folioId: folio.id,
          clientId: folio.clientId || currentScopedClient?.id || 'c1',
          clientName: folio.clientName || currentScopedClient?.name || 'Cliente',
          costCenterId: folio.costCenterId || currentDefaultCostCenter?.id || 'cc1',
          costCenterName: folio.costCenterName || currentDefaultCostCenter?.name || 'Centro de Costos',
          type: isDevolucion ? 'DEVOLUCION' : 'RETRABAJO',
          originStage: originStage,
          causeCategory: isDevolucion ? 'Retrabajo Cliente (Rechazo Planta)' : 'Incidencia de Inspección',
          causeDetail: stageEntry?.incidenceReport || stageEntry?.returnDetails || stageEntry?.notes || folio.notes || 'Defecto detectado en proceso de control.',
          affectedPieces: totalPieces,
          costEstimateMXN: 3500,
          responsibleDept: isDevolucion ? 'LOGISTICA_TRANSPORTE' : 'CALIDAD',
          priority: 'ALTA',
          status: folio.status === 'RETRABAJO' || folio.status === 'DEVOLUCION' ? 'PENDIENTE' : 'RESUELTO',
          reportedBy: stageEntry?.operatorName || 'Operador de Estación',
          dateReported: stageEntry?.timestamp || folio.createdDate,
          photos: folio.photoUrl ? [folio.photoUrl] : []
        });
      }
    });

    // If user is a client, they can see Retrabajos Cliente (DEVOLUCION) and Scrap/Item Dañado
    if (isUserClient) {
      list = list.filter(inc => inc.type === 'DEVOLUCION' || inc.type === 'SCRAP' || inc.originStage === 'SCRAP_ITEM_DANADO');
    }

    return list;
  }, [folios, incidents, currentScopedClient, currentDefaultCostCenter, isUserClient]);

  // Filter state
  const [activeTypeTab, setActiveTypeTab] = useState<'ALL' | 'RETRABAJO' | 'DEVOLUCION' | 'INSPECCION' | 'ENTREGA_PLANTA' | 'PENDIETES' | 'RESUELTOS'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  // Date Range filter states for table queries
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [datePreset, setDatePresetState] = useState<'ALL' | 'TODAY' | '7DAYS' | 'THIS_MONTH' | '30DAYS' | 'LAST_MONTH' | 'CUSTOM'>('ALL');

  const parseIncidentDate = (dateStr?: string): Date | null => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return d;
    const parts = dateStr.split(/[\/\-T\s]/);
    if (parts.length >= 3) {
      if (parts[0].length <= 2 && parts[2].length === 4) {
        const parsed = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
        if (!isNaN(parsed.getTime())) return parsed;
      }
    }
    return null;
  };

  const applyDatePreset = (preset: 'ALL' | 'TODAY' | '7DAYS' | 'THIS_MONTH' | '30DAYS' | 'LAST_MONTH') => {
    setDatePresetState(preset);
    const now = new Date();
    const formatYMD = (d: Date) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    if (preset === 'ALL') {
      setStartDate('');
      setEndDate('');
    } else if (preset === 'TODAY') {
      const today = formatYMD(now);
      setStartDate(today);
      setEndDate(today);
    } else if (preset === '7DAYS') {
      const past7 = new Date();
      past7.setDate(past7.getDate() - 6);
      setStartDate(formatYMD(past7));
      setEndDate(formatYMD(now));
    } else if (preset === 'THIS_MONTH') {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      setStartDate(formatYMD(firstDay));
      setEndDate(formatYMD(now));
    } else if (preset === '30DAYS') {
      const past30 = new Date();
      past30.setDate(past30.getDate() - 29);
      setStartDate(formatYMD(past30));
      setEndDate(formatYMD(now));
    } else if (preset === 'LAST_MONTH') {
      const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      setStartDate(formatYMD(firstDayLastMonth));
      setEndDate(formatYMD(lastDayLastMonth));
    }
  };

  const handleStartDateChange = (val: string) => {
    setStartDate(val);
    setDatePresetState('CUSTOM');
  };

  const handleEndDateChange = (val: string) => {
    setEndDate(val);
    setDatePresetState('CUSTOM');
  };

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setDatePresetState('ALL');
  };

  // Modals state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  const [historyFilterType, setHistoryFilterType] = useState<'ALL' | 'RETRABAJO' | 'DEVOLUCION' | 'SCRAP'>('ALL');
  const [selectedIncidentForDetail, setSelectedIncidentForDetail] = useState<ReworkIncident | null>(null);

  // Edit Modal state for full process modification
  const [editingIncident, setEditingIncident] = useState<ReworkIncident | null>(null);
  const [editFormData, setEditFormData] = useState({
    id: '',
    folioId: '',
    dateReported: '',
    type: 'RETRABAJO' as IncidentType,
    originStage: 'INSPECCION' as IncidentOrigin,
    clientId: '',
    clientName: '',
    costCenterId: '',
    costCenterName: '',
    causeCategory: 'Mala Adherencia de Pintura',
    causeDetail: '',
    reworkPiecesName: '',
    affectedPieces: 10,
    costEstimateMXN: 2500,
    responsibleDept: 'CALIDAD' as ResponsibleDept,
    priority: 'MEDIA' as IncidentPriority,
    status: 'PENDIENTE' as IncidentStatus,
    reportedBy: '',
    actionPlan: '',
    resolutionNotes: '',
    photos: ['', '', '', '', '', '', ''] as string[]
  });
  const editFileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Form State for new Incident with 7 Photos
  const [formData, setFormData] = useState({
    folioId: '',
    type: (isUserClient ? 'DEVOLUCION' : 'RETRABAJO') as IncidentType,
    originStage: (isUserClient ? 'ENTREGA_PLANTA' : 'INSPECCION') as IncidentOrigin,
    clientId: currentScopedClient?.id || clients[0]?.id || '',
    costCenterId: currentDefaultCostCenter?.id || costCenters[0]?.id || '',
    causeCategory: isUserClient ? 'MATERIAL DAÑADO' : 'Mala Adherencia de Pintura',
    causeDetail: '',
    reworkPiecesName: '',
    affectedPieces: 10,
    costEstimateMXN: 2500,
    responsibleDept: 'CALIDAD' as ResponsibleDept,
    priority: 'MEDIA' as IncidentPriority,
    actionPlan: '',
    photos: ['', '', '', '', '', '', ''] as string[] // 7 slot photos
  });

  const [reworkScope, setReworkScope] = useState<'TOTAL' | 'PARCIAL'>('TOTAL');

  // Match existing folio from entered/selected folioId
  const matchedFolio = useMemo(() => {
    if (!formData.folioId.trim()) return null;
    const cleaned = formData.folioId.trim().toLowerCase();
    return folios.find(f => {
      const fid = f.id.toLowerCase();
      return fid === cleaned || `f-${fid}` === cleaned || fid === `f-${cleaned}`;
    }) || null;
  }, [folios, formData.folioId]);

  // Keep form synced when opening modal
  useEffect(() => {
    if (isCreateModalOpen) {
      setReworkScope('TOTAL');
      setFormData(prev => ({
        ...prev,
        clientId: currentScopedClient?.id || clients[0]?.id || '',
        costCenterId: currentDefaultCostCenter?.id || availableCostCenters[0]?.id || costCenters[0]?.id || '',
        photos: ['', '', '', '', '', '', '']
      }));
    }
  }, [isCreateModalOpen, currentScopedClient, currentDefaultCostCenter, availableCostCenters, clients, costCenters]);

  // Auto-fill client, cost center and item info when a matching folio is selected
  useEffect(() => {
    if (matchedFolio) {
      const totalPieces = matchedFolio.items?.reduce((a, b) => a + (Number(b.quantity) || 0), 0) || 1;
      const itemsSummary = matchedFolio.items?.map(i => `${i.description} (${i.quantity} pzas)`).join(', ') || 'Todo el lote';

      setFormData(prev => ({
        ...prev,
        clientId: matchedFolio.clientId || prev.clientId,
        costCenterId: matchedFolio.costCenterId || prev.costCenterId,
        affectedPieces: reworkScope === 'TOTAL' ? totalPieces : prev.affectedPieces,
        reworkPiecesName: reworkScope === 'TOTAL' ? `Todo el Folio #${matchedFolio.id} (${itemsSummary})` : prev.reworkPiecesName
      }));
    }
  }, [matchedFolio, reworkScope]);

  // File Inputs references for 7 photos
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Filtered List
  const filteredIncidents = useMemo(() => {
    return combinedIncidents.filter(inc => {
      // Type or stage filter
      if (activeTypeTab === 'RETRABAJO' && inc.type !== 'RETRABAJO') return false;
      if (activeTypeTab === 'DEVOLUCION' && inc.type !== 'DEVOLUCION') return false;
      if (activeTypeTab === 'SCRAP' && inc.type !== 'SCRAP' && inc.originStage !== 'SCRAP_ITEM_DANADO') return false;
      if (activeTypeTab === 'INSPECCION' && inc.originStage !== 'INSPECCION') return false;
      if (activeTypeTab === 'ENTREGA_PLANTA' && inc.originStage !== 'ENTREGA_PLANTA') return false;
      if (activeTypeTab === 'PENDIETES' && inc.status !== 'PENDIENTE' && inc.status !== 'EN_REPROCESO') return false;
      if (activeTypeTab === 'RESUELTOS' && inc.status !== 'RESUELTO' && inc.status !== 'REINSPECCIONADO') return false;

      // Date Range filter for queries
      if (startDate || endDate) {
        const incDate = parseIncidentDate(inc.dateReported);
        if (incDate) {
          if (startDate) {
            const start = new Date(startDate + 'T00:00:00');
            if (incDate < start) return false;
          }
          if (endDate) {
            const end = new Date(endDate + 'T23:59:59.999');
            if (incDate > end) return false;
          }
        }
      }

      // Text Search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchId = inc.id.toLowerCase().includes(q) || inc.folioId.toLowerCase().includes(q);
        const matchClient = inc.clientName.toLowerCase().includes(q);
        const matchCause = inc.causeCategory.toLowerCase().includes(q) || inc.causeDetail.toLowerCase().includes(q);
        const matchReporter = inc.reportedBy.toLowerCase().includes(q);
        return matchId || matchClient || matchCause || matchReporter;
      }

      return true;
    });
  }, [combinedIncidents, activeTypeTab, searchQuery, startDate, endDate]);

  // Metrics
  const stats = useMemo(() => {
    const total = combinedIncidents.length;
    const retrabajos = combinedIncidents.filter(i => i.type === 'RETRABAJO');
    const devoluciones = combinedIncidents.filter(i => i.type === 'DEVOLUCION');
    const scraps = combinedIncidents.filter(i => i.type === 'SCRAP' || i.originStage === 'SCRAP_ITEM_DANADO');

    const fromInspeccion = combinedIncidents.filter(i => i.originStage === 'INSPECCION');
    const fromEntrega = combinedIncidents.filter(i => i.originStage === 'ENTREGA_PLANTA');
    const fromScrap = combinedIncidents.filter(i => i.originStage === 'SCRAP_ITEM_DANADO' || i.type === 'SCRAP');
    const resolved = combinedIncidents.filter(i => i.status === 'RESUELTO' || i.status === 'REINSPECCIONADO');
    const pending = combinedIncidents.filter(i => i.status === 'PENDIENTE');
    const inProgress = combinedIncidents.filter(i => i.status === 'EN_REPROCESO');
    const discarded = combinedIncidents.filter(i => i.status === 'DESECHADO');
    const totalCost = combinedIncidents.reduce((acc, curr) => acc + (curr.costEstimateMXN || 0), 0);
    const totalPieces = combinedIncidents.reduce((acc, curr) => acc + (curr.affectedPieces || 0), 0);
    const resolutionRate = total > 0 ? Math.round((resolved.length / total) * 100) : 100;

    return {
      total,
      totalRetrabajos: retrabajos.length,
      totalDevoluciones: devoluciones.length,
      totalScraps: scraps.length,
      fromInspeccion: fromInspeccion.length,
      fromEntrega: fromEntrega.length,
      fromScrap: fromScrap.length,
      resolvedCount: resolved.length,
      pendingCount: pending.length,
      inProgressCount: inProgress.length,
      discardedCount: discarded.length,
      totalCost,
      totalPieces,
      resolutionRate
    };
  }, [combinedIncidents]);

  // Chart Data
  const chartByOriginAndType = useMemo(() => {
    return [
      {
        fase: 'Inspección CC (Fase 4)',
        Retrabajos: combinedIncidents.filter(i => i.originStage === 'INSPECCION' && i.type === 'RETRABAJO').length,
        Devoluciones: combinedIncidents.filter(i => i.originStage === 'INSPECCION' && i.type === 'DEVOLUCION').length
      },
      {
        fase: 'Entrega a Planta (Fase 6)',
        Retrabajos: combinedIncidents.filter(i => i.originStage === 'ENTREGA_PLANTA' && i.type === 'RETRABAJO').length,
        Devoluciones: combinedIncidents.filter(i => i.originStage === 'ENTREGA_PLANTA' && i.type === 'DEVOLUCION').length
      },
      {
        fase: 'SCRAP/Item Dañado',
        Retrabajos: 0,
        Devoluciones: combinedIncidents.filter(i => i.originStage === 'SCRAP_ITEM_DANADO' || i.type === 'SCRAP').length
      }
    ];
  }, [combinedIncidents]);

  const chartByCause = useMemo(() => {
    const causeMap: Record<string, number> = {};
    combinedIncidents.forEach(inc => {
      const cat = inc.causeCategory || 'Otro Defecto';
      causeMap[cat] = (causeMap[cat] || 0) + 1;
    });

    return Object.entries(causeMap).map(([name, count]) => ({ name, count }));
  }, [combinedIncidents]);

  const chartByStatusPie = useMemo(() => {
    const statusMap: Record<string, number> = {
      'Pendiente': 0,
      'En Reproceso': 0,
      'Resuelto': 0,
      'Desechado': 0
    };

    combinedIncidents.forEach(inc => {
      if (inc.status === 'PENDIENTE') statusMap['Pendiente']++;
      else if (inc.status === 'EN_REPROCESO') statusMap['En Reproceso']++;
      else if (inc.status === 'RESUELTO' || inc.status === 'REINSPECCIONADO') statusMap['Resuelto']++;
      else if (inc.status === 'DESECHADO') statusMap['Desechado']++;
    });

    return [
      { name: 'Pendiente', value: statusMap['Pendiente'], color: COLORS.pendiente },
      { name: 'En Reproceso', value: statusMap['En Reproceso'], color: COLORS.enReproceso },
      { name: 'Resuelto', value: statusMap['Resuelto'], color: COLORS.resuelto },
      { name: 'Desechado', value: statusMap['Desechado'], color: COLORS.desechado }
    ].filter(d => d.value > 0);
  }, [combinedIncidents]);

  // Strategic Executive Report PDF Generator State & Handler
  const [isGeneratingStrategicPDF, setIsGeneratingStrategicPDF] = useState(false);

  const handleDownloadStrategicReportPDF = async () => {
    try {
      setIsGeneratingStrategicPDF(true);

      // Date range label
      const dateRangeLabel = startDate && endDate
        ? `${startDate} al ${endDate}`
        : startDate
        ? `A partir de ${startDate}`
        : endDate
        ? `Hasta ${endDate}`
        : 'Histórico Completo Consolidado';

      // For clients, ensure only DEVOLUCIONES are exported
      const incidentsForReport = isUserClient
        ? filteredIncidents.filter(i => i.type === 'DEVOLUCION' || i.originStage === 'ENTREGA_PLANTA')
        : filteredIncidents;

      await downloadReworksAndReturnsStrategicReportPDF({
        clientName: currentScopedClient?.name || (isUserClient ? 'CLIENTE' : 'CONSOLIDADO GENERAL'),
        costCenterName: currentDefaultCostCenter?.name,
        incidents: incidentsForReport,
        totalFolios: folios.length,
        stats: {
          total: stats.total,
          totalRetrabajos: stats.totalRetrabajos,
          totalDevoluciones: stats.totalDevoluciones,
          fromInspeccion: stats.fromInspeccion,
          fromEntrega: stats.fromEntrega,
          totalPieces: stats.totalPieces,
          pendingCount: stats.pendingCount,
          inProgressCount: stats.inProgressCount,
          resolvedCount: stats.resolvedCount,
          discardedCount: stats.discardedCount,
        },
        chartByOriginAndType,
        chartByCause,
        chartByStatusPie,
        dateRange: dateRangeLabel,
        generatedBy: currentUser?.name || (isUserClient ? 'Portal Cliente' : 'Dirección de Operaciones & Calidad SNF'),
        isUserClient
      });
      window.showAppAlert?.('Reporte estratégico descargado exitosamente en formato PDF con formato ejecutivo y gráficas.', 'Descarga Exitosa');
    } catch (err) {
      console.error('Error generando reporte estratégico:', err);
      window.showAppAlert?.('Ocurrió un error al generar el PDF del reporte estratégico.', 'Error de Descarga');
    } finally {
      setIsGeneratingStrategicPDF(false);
    }
  };

  // Handle Photo Slot Change (File -> DataURL)
  const handlePhotoUpload = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      if (base64) {
        setFormData(prev => {
          const newPhotos = [...prev.photos];
          newPhotos[index] = base64;
          return { ...prev, photos: newPhotos };
        });
      }
    };
    reader.readAsDataURL(file);
  };

  // Remove Photo Slot
  const handleRemovePhoto = (index: number) => {
    setFormData(prev => {
      const newPhotos = [...prev.photos];
      newPhotos[index] = '';
      return { ...prev, photos: newPhotos };
    });
  };

  // Handle Save New Incident
  const handleSaveNewIncident = (e: React.FormEvent) => {
    e.preventDefault();

    const selectedClient = currentScopedClient || clients.find(c => c.id === formData.clientId) || clients[0];
    const selectedCostCenterObj = availableCostCenters.find(cc => cc.id === formData.costCenterId) || currentDefaultCostCenter || costCenters[0];

    const targetFolioId = formData.folioId.trim() || `F-${Math.floor(100000 + Math.random() * 900000)}`;
    const validPhotos = formData.photos.filter(p => !!p);

    const newIncident: ReworkIncident = {
      id: `INC-${Date.now().toString().slice(-6)}`,
      folioId: targetFolioId,
      clientId: selectedClient?.id || 'c1',
      clientName: selectedClient?.name || 'Cliente',
      costCenterId: selectedCostCenterObj?.id || 'cc1',
      costCenterName: selectedCostCenterObj?.name || 'Centro de Costos',
      type: formData.type,
      originStage: formData.originStage,
      causeCategory: formData.causeCategory,
      causeDetail: formData.causeDetail || 'Incidencia registrada manualmente con evidencia de fotos.',
      affectedPieces: Number(formData.affectedPieces) || 1,
      reworkPiecesName: formData.reworkPiecesName || 'Piezas con defecto de inspección',
      costEstimateMXN: Number(formData.costEstimateMXN) || 0,
      responsibleDept: formData.responsibleDept,
      priority: formData.priority,
      status: 'PENDIENTE',
      reportedBy: currentUser?.name || 'Operador Control Calidad',
      dateReported: new Date().toISOString(),
      actionPlan: formData.actionPlan,
      photos: validPhotos
    };

    const updatedIncidents = [newIncident, ...incidents];
    setIncidents(updatedIncidents);
    // Real-time sync to Firestore & 24/7 dual persistence
    dataService.saveIncident(newIncident).catch(err => {
      console.warn('Error saving incident to Firestore:', err);
    });
    try {
      localStorage.setItem('sic_pro_rework_incidents', JSON.stringify(updatedIncidents));
      localStorage.setItem('sic_pro_incidents', JSON.stringify(updatedIncidents));
      window.dispatchEvent(new Event('rework_incidents_updated'));
      window.dispatchEvent(new CustomEvent('rework_incidents_updated', { detail: newIncident }));
    } catch (err) {
      console.warn('Error saving new incident to localStorage:', err);
    }

    // Check if targetFolioId matches an existing folio to update its status
    const existingFolio = folios.find(f => f.id === targetFolioId || (f as any).folioNumber === targetFolioId);
    const targetStage: ProcessStage = (formData.type === 'DEVOLUCION' || formData.type === 'SCRAP') ? 'DEVOLUCION' : 'RETRABAJO';

    if (existingFolio) {
      const updatedHistory: StageHistory[] = [
        ...(existingFolio.history || []),
        {
          stage: targetStage,
          timestamp: new Date().toISOString(),
          operatorName: currentUser?.name || 'Operador',
          incidenceReport: `${formData.type}: ${formData.causeCategory} - ${formData.causeDetail}`,
          notes: formData.actionPlan
        }
      ];

      onUpdateFolio({
        ...existingFolio,
        status: targetStage,
        history: updatedHistory,
        photoUrl: validPhotos[0] || existingFolio.photoUrl
      });
    } else if (onCreateFolio) {
      onCreateFolio({
        id: targetFolioId,
        qrCodeUrl: '',
        clientId: selectedClient?.id || 'c1',
        clientName: selectedClient?.name || 'Cliente',
        costCenterId: selectedCostCenterObj?.id || 'cc1',
        costCenterName: selectedCostCenterObj?.name || 'Centro de Costos',
        status: targetStage,
        date: new Date().toISOString().slice(0, 10),
        createdDate: new Date().toISOString(),
        createdBy: currentUser?.name || 'Operador',
        hasGranallado: false,
        items: [{
          goodsItemId: `item-${Date.now()}`,
          description: formData.reworkPiecesName || 'Piezas en Retrabajo / Scrap',
          quantity: Number(formData.affectedPieces) || 1,
          unitPriceMXN: 0,
          unitPriceUSD: 0,
          totalMXN: 0,
          totalUSD: 0
        }],
        history: [{
          stage: targetStage,
          timestamp: new Date().toISOString(),
          operatorName: currentUser?.name || 'Operador',
          incidenceReport: `${formData.type}: ${formData.causeCategory} - ${formData.causeDetail}`,
          notes: formData.actionPlan
        }],
        photoUrl: validPhotos[0] || ''
      });
    }

    setIsCreateModalOpen(false);
  };

  // Status Change Handler for Incidents
  const handleUpdateIncidentStatus = (incidentId: string, newStatus: IncidentStatus, notes?: string) => {
    setIncidents(prev => prev.map(inc => {
      if (inc.id === incidentId) {
        return {
          ...inc,
          status: newStatus,
          resolutionNotes: notes || inc.resolutionNotes
        };
      }
      return inc;
    }));

    // If linked to a Folio and resolved, restore the Folio's process stage
    const targetIncident = combinedIncidents.find(i => i.id === incidentId);
    if (targetIncident) {
      const updatedIncObj: ReworkIncident = {
        ...targetIncident,
        status: newStatus,
        resolutionNotes: notes || targetIncident.resolutionNotes
      };
      dataService.saveIncident(updatedIncObj).catch(err => console.warn(err));

      const folio = folios.find(f => f.id === targetIncident.folioId);
      if (folio) {
        let returnStage: ProcessStage = 'INSPECCION';
        if (newStatus === 'RESUELTO' || newStatus === 'REINSPECCIONADO') {
          returnStage = targetIncident.originStage === 'ENTREGA_PLANTA' ? 'ENTREGA_PLANTA' : 'INSPECCION';
        }

        const updatedHistory: StageHistory[] = [
          ...(folio.history || []),
          {
            stage: returnStage,
            timestamp: new Date().toISOString(),
            operatorName: currentUser?.name || 'Supervisor Calidad',
            notes: `Incidencia ${targetIncident.id} (${newStatus}): ${notes || 'Resuelto y reinspeccionado exitosamente.'}`
          }
        ];

        onUpdateFolio({
          ...folio,
          status: returnStage,
          history: updatedHistory
        });
      }
    }

    if (selectedIncidentForDetail?.id === incidentId) {
      setSelectedIncidentForDetail(prev => prev ? { ...prev, status: newStatus, resolutionNotes: notes || prev.resolutionNotes } : null);
    }
  };

  // Generate PDF report
  const handleDownloadPDF = (inc: ReworkIncident) => {
    downloadReworkIncidentPDF({
      id: inc.id,
      folioId: inc.folioId,
      clientId: inc.clientId,
      clientName: inc.clientName,
      costCenterId: inc.costCenterId,
      costCenterName: inc.costCenterName,
      type: inc.type,
      originStage: inc.originStage,
      causeCategory: inc.causeCategory,
      causeDetail: inc.causeDetail,
      affectedPieces: inc.affectedPieces,
      costEstimateMXN: inc.costEstimateMXN,
      responsibleDept: inc.responsibleDept,
      priority: inc.priority,
      status: inc.status,
      reportedBy: inc.reportedBy,
      dateReported: inc.dateReported,
      actionPlan: inc.actionPlan,
      resolutionNotes: inc.resolutionNotes,
      photoUrl: inc.photoUrl,
      photos: inc.photos
    });
  };

  // Open Edit Modal with full incident data
  const handleOpenEditModal = (inc: ReworkIncident) => {
    const currentPhotos = (inc.photos && inc.photos.length > 0)
      ? [...inc.photos]
      : inc.photoUrl ? [inc.photoUrl] : [];
    const photos7 = Array(7).fill('').map((_, i) => currentPhotos[i] || '');

    let dateVal = inc.dateReported;
    if (dateVal) {
      const d = new Date(dateVal);
      if (!isNaN(d.getTime())) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        dateVal = `${y}-${m}-${day}`;
      }
    }

    setEditFormData({
      id: inc.id,
      folioId: inc.folioId || '',
      dateReported: dateVal || new Date().toISOString().slice(0, 10),
      type: inc.type,
      originStage: inc.originStage,
      clientId: inc.clientId || clients[0]?.id || '',
      clientName: inc.clientName || clients[0]?.name || '',
      costCenterId: inc.costCenterId || costCenters[0]?.id || '',
      costCenterName: inc.costCenterName || costCenters[0]?.name || '',
      causeCategory: inc.causeCategory || 'Mala Adherencia de Pintura',
      causeDetail: inc.causeDetail || '',
      reworkPiecesName: inc.reworkPiecesName || '',
      affectedPieces: inc.affectedPieces || 1,
      costEstimateMXN: inc.costEstimateMXN || 0,
      responsibleDept: inc.responsibleDept || 'CALIDAD',
      priority: inc.priority || 'MEDIA',
      status: inc.status || 'PENDIENTE',
      reportedBy: inc.reportedBy || currentUser?.name || 'Control de Calidad',
      actionPlan: inc.actionPlan || '',
      resolutionNotes: inc.resolutionNotes || '',
      photos: photos7
    });
    setEditingIncident(inc);
  };

  // Handle Photo Slot Change in Edit Modal
  const handleEditPhotoUpload = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      if (base64) {
        setEditFormData(prev => {
          const newPhotos = [...prev.photos];
          newPhotos[index] = base64;
          return { ...prev, photos: newPhotos };
        });
      }
    };
    reader.readAsDataURL(file);
  };

  // Remove Photo Slot in Edit Modal
  const handleEditRemovePhoto = (index: number) => {
    setEditFormData(prev => {
      const newPhotos = [...prev.photos];
      newPhotos[index] = '';
      return { ...prev, photos: newPhotos };
    });
  };

  // Save Edit Incident Changes
  const handleSaveEditIncident = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingIncident) return;

    const selectedClient = clients.find(c => c.id === editFormData.clientId) || { id: editFormData.clientId, name: editFormData.clientName || 'Cliente' };
    const selectedCostCenter = costCenters.find(cc => cc.id === editFormData.costCenterId) || { id: editFormData.costCenterId, name: editFormData.costCenterName || 'Centro de Costos' };
    const validPhotos = editFormData.photos.filter(p => Boolean(p));

    const updatedIncident: ReworkIncident = {
      id: editFormData.id,
      folioId: editFormData.folioId.trim() || editingIncident.folioId,
      clientId: selectedClient.id,
      clientName: selectedClient.name,
      costCenterId: selectedCostCenter.id,
      costCenterName: selectedCostCenter.name,
      type: editFormData.type,
      originStage: editFormData.originStage,
      causeCategory: editFormData.causeCategory,
      causeDetail: editFormData.causeDetail || 'Incidencia modificada por control de calidad.',
      affectedPieces: Number(editFormData.affectedPieces) || 1,
      reworkPiecesName: editFormData.reworkPiecesName || 'Piezas en proceso de retrabajo',
      costEstimateMXN: Number(editFormData.costEstimateMXN) || 0,
      responsibleDept: editFormData.responsibleDept,
      priority: editFormData.priority,
      status: editFormData.status,
      reportedBy: editFormData.reportedBy || currentUser?.name || 'Control de Calidad',
      dateReported: editFormData.dateReported.includes('T') ? editFormData.dateReported : new Date(editFormData.dateReported + 'T12:00:00').toISOString(),
      actionPlan: editFormData.actionPlan,
      resolutionNotes: editFormData.resolutionNotes,
      photoUrl: validPhotos[0] || undefined,
      photos: validPhotos
    };

    setIncidents(prev => {
      const exists = prev.some(i => i.id === updatedIncident.id);
      if (exists) {
        return prev.map(i => i.id === updatedIncident.id ? updatedIncident : i);
      } else {
        return [updatedIncident, ...prev];
      }
    });

    // Real-time synchronization to Firestore & 24/7 dual persistence
    dataService.saveIncident(updatedIncident).catch(err => {
      console.warn('Error saving updated incident to Firestore:', err);
    });

    // Check if folio is in folios list and update
    const targetFolio = folios.find(f => f.id === updatedIncident.folioId || f.id === editingIncident.folioId);
    if (targetFolio && onUpdateFolio) {
      const targetStage: ProcessStage = updatedIncident.status === 'RESUELTO' || updatedIncident.status === 'REINSPECCIONADO'
        ? (updatedIncident.originStage === 'ENTREGA_PLANTA' ? 'ENTREGA_PLANTA' : 'INSPECCION')
        : (updatedIncident.type === 'DEVOLUCION' ? 'DEVOLUCION' : 'RETRABAJO');
      
      const updatedHistory = [...(targetFolio.history || [])];
      const stageIdx = updatedHistory.findIndex(h => h.stage === 'RETRABAJO' || h.stage === 'DEVOLUCION');
      if (stageIdx >= 0) {
        updatedHistory[stageIdx] = {
          ...updatedHistory[stageIdx],
          incidenceReport: `${updatedIncident.type}: ${updatedIncident.causeCategory} - ${updatedIncident.causeDetail}`,
          notes: updatedIncident.actionPlan,
          operatorName: updatedIncident.reportedBy
        };
      }

      onUpdateFolio({
        ...targetFolio,
        status: targetStage,
        history: updatedHistory,
        photoUrl: validPhotos[0] || targetFolio.photoUrl,
        notes: updatedIncident.actionPlan || targetFolio.notes
      });
    }

    if (selectedIncidentForDetail && selectedIncidentForDetail.id === updatedIncident.id) {
      setSelectedIncidentForDetail(updatedIncident);
    }

    setEditingIncident(null);
  };

  return (
    <div className="space-y-6 text-white pb-12 font-sans">
      
      {/* MODULE HEADER BAR */}
      <div className="bg-[#1f201f] border border-[#3a3d3a] rounded-2xl p-5 shadow-2xl flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 bg-brand-red/20 border border-brand-red/40 text-brand-red text-[10px] font-mono font-bold rounded-md uppercase tracking-wider flex items-center gap-1">
              <AlertOctagon className="h-3.5 w-3.5 animate-pulse" />
              GESTIÓN DE REPROCESOS Y RETRABAJOS
            </span>
            {currentScopedClient && (
              <span className="text-[10px] bg-brand-red text-white border border-brand-red font-mono font-black px-2.5 py-0.5 rounded-md flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                CLIENTE: {currentScopedClient.name.toUpperCase()}
              </span>
            )}
          </div>
          <h1 className="text-xl md:text-2xl font-black text-white tracking-tight flex items-center gap-2">
            {isUserClient ? 'Retrabajos de Cliente y Scrap' : 'Reprocesos y Retrabajos'}
          </h1>
          <p className="text-xs text-slate-400 font-medium">
            {isUserClient
              ? <>Control de retrabajos de cliente e incidencias originadas en <strong className="text-emerald-400">Entrega a Planta (Fase 6)</strong>.</>
              : <>Control de reprocesos técnicos e incidencias originadas en <strong className="text-purple-400">Inspección CC (Fase 4)</strong> y <strong className="text-emerald-400">Entrega a Planta (Fase 6)</strong>.</>
            }
          </p>
        </div>

        <div className="flex items-center gap-2.5 shrink-0 flex-wrap">
          {/* Strategic Executive PDF Report Button */}
          <button
            onClick={handleDownloadStrategicReportPDF}
            disabled={isGeneratingStrategicPDF}
            className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white text-xs font-black rounded-xl shadow-lg shadow-emerald-950/40 border border-emerald-400/30 flex items-center gap-2 transition-all cursor-pointer active:scale-95 group hover:scale-[1.02] disabled:opacity-50"
            title="Descargar Dashboard Completo en PDF como Reporte Estratégico para Dirección"
          >
            <FileDown className={`h-4 w-4 text-emerald-200 group-hover:scale-110 transition-transform ${isGeneratingStrategicPDF ? 'animate-bounce' : ''}`} />
            <span>{isGeneratingStrategicPDF ? 'Generando PDF...' : 'Descargar Reporte Estratégico (PDF)'}</span>
          </button>

          <button
            onClick={() => setIsHistoryModalOpen(true)}
            className="px-4 py-2 bg-gradient-to-r from-amber-500 via-orange-500 to-red-600 hover:from-amber-600 hover:to-red-700 text-white text-xs font-black rounded-xl shadow-lg shadow-orange-950/40 border border-amber-300/30 flex items-center gap-2 transition-all cursor-pointer active:scale-95 group hover:scale-[1.02]"
          >
            <FolderClock className="h-4 w-4 text-amber-100 group-hover:rotate-12 transition-transform" />
            <span>Historial de Retrabajos</span>
            <span className="bg-black/30 text-amber-200 text-[10px] font-mono font-bold px-2 py-0.5 rounded-full ml-0.5 border border-amber-300/20">
              {combinedIncidents.length}
            </span>
          </button>

          {isUserClient ? (
            <button
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  type: 'DEVOLUCION',
                  originStage: 'ENTREGA_PLANTA',
                  causeCategory: 'MATERIAL DAÑADO'
                }));
                setIsCreateModalOpen(true);
              }}
              className="px-4 py-2 bg-brand-red hover:bg-red-700 text-white text-xs font-bold rounded-xl shadow-lg flex items-center gap-2 transition-all cursor-pointer active:scale-95"
            >
              <Plus className="h-4 w-4" />
              <span>Registrar Retrabajo Cliente / Scrap (7 Fotos)</span>
            </button>
          ) : (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-4 py-2 bg-brand-red hover:bg-red-700 text-white text-xs font-bold rounded-xl shadow-lg flex items-center gap-2 transition-all cursor-pointer active:scale-95"
            >
              <Plus className="h-4 w-4" />
              <span>Registrar Incidencia Manual (7 Fotos)</span>
            </button>
          )}
        </div>
      </div>

      {/* EXECUTIVE KPI METRICS BAR */}
      <div className={`grid gap-3 ${isUserClient ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 md:grid-cols-3 xl:grid-cols-5'}`}>
        {/* Metric 1 */}
        <div className="bg-[#242524] border border-[#3a3d3a] rounded-xl p-3.5 shadow-md space-y-1">
          <div className="flex items-center justify-between text-slate-400 text-[10px] font-bold font-mono uppercase">
            <span>{isUserClient ? 'Total Retrabajos' : 'Total Incidencias'}</span>
            <Layers className="h-4 w-4 text-slate-400" />
          </div>
          <div className="text-2xl font-black text-white">{stats.total}</div>
          <p className="text-[10px] text-slate-400 font-medium">
            {isUserClient ? 'Registradas en entrega a planta' : `Registradas para ${currentScopedClient?.name || 'Cliente'}`}
          </p>
        </div>

        {/* Metric 2: Retrabajos (Internal Only) */}
        {!isUserClient && (
          <div className="bg-[#242524] border border-[#3a3d3a] rounded-xl p-3.5 shadow-md space-y-1">
            <div className="flex items-center justify-between text-rose-400 text-[10px] font-bold font-mono uppercase">
              <span>Retrabajos Internos</span>
              <RefreshCw className="h-4 w-4 text-rose-400" />
            </div>
            <div className="text-2xl font-black text-rose-400">{stats.totalRetrabajos}</div>
            <div className="flex items-center gap-1.5 text-[9.5px]">
              <span className="text-purple-300 font-bold">🔍 {stats.fromInspeccion} Insp</span>
              <span className="text-slate-500">•</span>
              <span className="text-emerald-300 font-bold">🚚 {stats.totalRetrabajos - stats.fromInspeccion} Entr</span>
            </div>
          </div>
        )}

        {/* Metric 3 */}
        <div className="bg-[#242524] border border-[#3a3d3a] rounded-xl p-3.5 shadow-md space-y-1">
          <div className="flex items-center justify-between text-red-400 text-[10px] font-bold font-mono uppercase">
            <span>Retrabajos Cliente</span>
            <Undo2 className="h-4 w-4 text-red-400" />
          </div>
          <div className="text-2xl font-black text-red-400">{stats.totalDevoluciones}</div>
          <p className="text-[10px] text-emerald-400 font-bold">🚚 Entrega a Planta</p>
        </div>

        {/* Metric 4 */}
        <div className="bg-[#242524] border border-[#3a3d3a] rounded-xl p-3.5 shadow-md space-y-1">
          <div className="flex items-center justify-between text-emerald-400 text-[10px] font-bold font-mono uppercase">
            <span>Tasa Resueltos</span>
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
          </div>
          <div className="text-2xl font-black text-emerald-400">{stats.resolutionRate}%</div>
          <p className="text-[10px] text-slate-400 font-medium">{stats.resolvedCount} de {stats.total} resueltos</p>
        </div>

        {/* Metric 5 */}
        <div className="bg-[#242524] border border-[#3a3d3a] rounded-xl p-3.5 shadow-md space-y-1">
          <div className="flex items-center justify-between text-amber-400 text-[10px] font-bold font-mono uppercase">
            <span>Piezas Afectadas</span>
            <Package className="h-4 w-4 text-amber-400" />
          </div>
          <div className="text-2xl font-black text-amber-300">{stats.totalPieces} pcs</div>
          <p className="text-[10px] text-slate-400 font-medium">En proceso de atención</p>
        </div>
      </div>

      {/* ANALYTICS CHARTS SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        
        {/* Chart 1: Reprocesos y Devoluciones por Fase de Origen */}
        <div className="bg-[#242524] border border-[#3a3d3a] rounded-2xl p-4 shadow-xl space-y-3">
          <div className="flex items-center justify-between border-b border-[#3a3d3a] pb-2.5">
            <div className="flex items-center gap-2">
              <BarChart2 className="h-4 w-4 text-brand-red" />
              <h3 className="text-xs font-bold text-white uppercase tracking-wider font-mono">Origen por Fase de Proceso</h3>
            </div>
            <span className="text-[9px] text-slate-400 bg-[#1a1b1a] px-2 py-0.5 rounded font-mono">Inspección vs Entrega</span>
          </div>

          <div className="h-52 w-full pt-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartByOriginAndType} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3a3d3a" vertical={false} />
                <XAxis dataKey="fase" stroke="#9a9d9a" tick={{ fontSize: 10 }} />
                <YAxis stroke="#9a9d9a" tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1b1a', borderColor: '#3a3d3a', borderRadius: '8px', fontSize: '11px', color: '#fff' }}
                />
                <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '8px' }} />
                <Bar dataKey="Retrabajos" fill={COLORS.retrabajo} radius={[4, 4, 0, 0]} />
                <Bar dataKey="Devoluciones" fill={COLORS.devolucion} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 2: Causa Raíz Frecuente */}
        <div className="bg-[#242524] border border-[#3a3d3a] rounded-2xl p-4 shadow-xl space-y-3">
          <div className="flex items-center justify-between border-b border-[#3a3d3a] pb-2.5">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-amber-400" />
              <h3 className="text-xs font-bold text-white uppercase tracking-wider font-mono">Análisis de Causa Raíz</h3>
            </div>
            <span className="text-[9px] text-slate-400 bg-[#1a1b1a] px-2 py-0.5 rounded font-mono">Top Defectos</span>
          </div>

          <div className="h-52 w-full pt-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartByCause} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3a3d3a" horizontal={false} />
                <XAxis type="number" stroke="#9a9d9a" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis dataKey="name" type="category" stroke="#9a9d9a" tick={{ fontSize: 9 }} width={100} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1b1a', borderColor: '#3a3d3a', borderRadius: '8px', fontSize: '11px', color: '#fff' }}
                />
                <Bar dataKey="count" name="Casos" fill="#f59e0b" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 3: Estado de Resolución */}
        <div className="bg-[#242524] border border-[#3a3d3a] rounded-2xl p-4 shadow-xl space-y-3">
          <div className="flex items-center justify-between border-b border-[#3a3d3a] pb-2.5">
            <div className="flex items-center gap-2">
              <PieIcon className="h-4 w-4 text-emerald-400" />
              <h3 className="text-xs font-bold text-white uppercase tracking-wider font-mono">Estado de Resolución</h3>
            </div>
            <span className="text-[9px] text-slate-400 bg-[#1a1b1a] px-2 py-0.5 rounded font-mono">Avances %</span>
          </div>

          <div className="h-52 w-full pt-2 flex items-center justify-center">
            {chartByStatusPie.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartByStatusPie}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={75}
                    paddingAngle={4}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {chartByStatusPie.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1b1a', borderColor: '#3a3d3a', borderRadius: '8px', fontSize: '11px', color: '#fff' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-xs text-slate-500 py-12">No hay incidencias registradas</div>
            )}
          </div>
        </div>

      </div>

      {/* FILTER & SEARCH CONTROL TABS */}
      <div className="bg-[#242524] border border-[#3a3d3a] rounded-2xl p-4 shadow-xl space-y-3.5">
        <div className="flex flex-col md:flex-row items-center justify-between gap-3">
          
          {/* Quick Filter Pills */}
          <div className="flex flex-wrap items-center gap-1.5 w-full md:w-auto">
            {(isUserClient
              ? [
                  { id: 'ALL', label: 'Todos los Retrabajos y Scrap', icon: Layers },
                  { id: 'DEVOLUCION', label: 'Retrabajos Cliente', icon: Undo2, color: 'text-red-400' },
                  { id: 'SCRAP', label: 'Scrap / Dañado', icon: Trash2, color: 'text-amber-400' },
                  { id: 'ENTREGA_PLANTA', label: '🚚 Fase Entrega Planta', icon: Truck },
                  { id: 'PENDIETES', label: '⏳ Pendientes', icon: AlertTriangle },
                  { id: 'RESUELTOS', label: '✅ Resueltos', icon: CheckCircle2 }
                ]
              : [
                  { id: 'ALL', label: 'Todos', icon: Layers },
                  { id: 'RETRABAJO', label: 'Reprocesos Internos', icon: RefreshCw, color: 'text-rose-400' },
                  { id: 'DEVOLUCION', label: 'Retrabajos Cliente', icon: Undo2, color: 'text-red-400' },
                  { id: 'SCRAP', label: 'Scrap / Dañado', icon: Trash2, color: 'text-amber-400' },
                  { id: 'INSPECCION', label: '🔍 Fase Inspección', icon: Eye },
                  { id: 'ENTREGA_PLANTA', label: '🚚 Fase Entrega Planta', icon: Truck },
                  { id: 'PENDIETES', label: '⏳ Pendientes', icon: AlertTriangle },
                  { id: 'RESUELTOS', label: '✅ Resueltos', icon: CheckCircle2 }
                ]
            ).map(tab => {
              const Icon = tab.icon;
              const isActive = activeTypeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTypeTab(tab.id as any)}
                  className={`px-3 py-1.5 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                    isActive
                      ? 'bg-brand-red text-white shadow-md'
                      : 'bg-[#1a1b1a] text-slate-300 hover:bg-[#2e302e] hover:text-white border border-[#3a3d3a]'
                  }`}
                >
                  <Icon className={`h-3.5 w-3.5 ${tab.color || ''}`} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Search Input & Locked Client Display */}
          <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <Search className="h-3.5 w-3.5 text-slate-400 absolute left-3 top-2.5" />
              <input
                type="text"
                placeholder="Buscar por Folio, Causa, Defecto..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full bg-[#1a1b1a] border border-[#3a3d3a] text-white text-xs pl-8 pr-3 py-2 rounded-xl focus:outline-none focus:border-brand-red transition-all"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-2.5 text-slate-400 hover:text-white">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {currentScopedClient ? (
              <div className="bg-[#1a1b1a] border border-brand-red/50 text-white text-xs px-3 py-2 rounded-xl font-bold flex items-center gap-1.5 shrink-0">
                <Building2 className="h-3.5 w-3.5 text-brand-red" />
                <span>{currentScopedClient.name}</span>
              </div>
            ) : null}
          </div>

        </div>

        {/* DATE RANGE FILTER ROW FOR CONSULTAS */}
        <div className="pt-2.5 border-t border-[#343734] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 text-xs">
          {/* Preset Buttons */}
          <div className="flex flex-wrap items-center gap-1.5">
            <div className="flex items-center gap-1 text-[11px] font-bold text-slate-400 mr-1 shrink-0 font-mono uppercase tracking-wider">
              <Calendar className="h-3.5 w-3.5 text-amber-400" />
              <span>Rango de Fechas:</span>
            </div>
            {[
              { id: 'ALL', label: 'Todas las fechas' },
              { id: 'TODAY', label: 'Hoy' },
              { id: '7DAYS', label: 'Últimos 7 días' },
              { id: 'THIS_MONTH', label: 'Este Mes' },
              { id: '30DAYS', label: 'Últimos 30 días' },
              { id: 'LAST_MONTH', label: 'Mes Anterior' }
            ].map(p => {
              const isSelected = datePreset === p.id && (p.id !== 'ALL' || (!startDate && !endDate));
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyDatePreset(p.id as any)}
                  className={`px-2.5 py-1 rounded-lg text-[10.5px] font-bold transition-colors cursor-pointer ${
                    isSelected
                      ? 'bg-amber-500/20 text-amber-300 border border-amber-500/50 shadow-sm'
                      : 'bg-[#181918] text-slate-300 hover:text-white hover:bg-[#2c2e2c] border border-[#383b38]'
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* Custom Date Pickers & Reset */}
          <div className="flex items-center flex-wrap gap-2 w-full lg:w-auto">
            <div className="flex items-center gap-1.5 bg-[#181918] border border-[#383b38] px-2.5 py-1 rounded-xl">
              <span className="text-[10.5px] text-slate-400 font-medium">Desde:</span>
              <input
                type="date"
                value={startDate}
                onChange={e => handleStartDateChange(e.target.value)}
                style={{ colorScheme: 'dark' }}
                className="bg-transparent text-white text-[11px] font-mono focus:outline-none cursor-pointer"
              />
            </div>

            <div className="flex items-center gap-1.5 bg-[#181918] border border-[#383b38] px-2.5 py-1 rounded-xl">
              <span className="text-[10.5px] text-slate-400 font-medium">Hasta:</span>
              <input
                type="date"
                value={endDate}
                onChange={e => handleEndDateChange(e.target.value)}
                style={{ colorScheme: 'dark' }}
                className="bg-transparent text-white text-[11px] font-mono focus:outline-none cursor-pointer"
              />
            </div>

            {(startDate || endDate) && (
              <button
                type="button"
                onClick={clearDateFilter}
                className="bg-red-950/60 hover:bg-red-900/80 text-red-300 border border-red-800/80 text-[10.5px] font-bold px-2.5 py-1 rounded-xl flex items-center gap-1 transition-all cursor-pointer shrink-0"
                title="Limpiar filtro de fechas"
              >
                <X className="h-3 w-3" />
                <span>Limpiar</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* INCIDENTS TABLE LIST */}
      <div className="bg-[#242524] border border-[#3a3d3a] rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-4 border-b border-[#3a3d3a] flex flex-wrap items-center justify-between gap-2 bg-[#1f201f]">
          <div className="flex items-center gap-2 flex-wrap">
            <AlertOctagon className="h-4 w-4 text-brand-red" />
            <h2 className="text-xs font-bold text-white uppercase tracking-wider font-mono">
              Registros de Reprocesos y Retrabajos ({filteredIncidents.length})
            </h2>
            {(startDate || endDate) && (
              <span className="text-[10px] bg-amber-500/20 text-amber-300 border border-amber-500/40 px-2 py-0.5 rounded-full font-mono font-bold flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {startDate && endDate ? `${startDate} al ${endDate}` : startDate ? `Desde ${startDate}` : `Hasta ${endDate}`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2.5">
            <button
              onClick={handleDownloadStrategicReportPDF}
              disabled={isGeneratingStrategicPDF}
              className="px-3 py-1.5 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white text-[11px] font-bold rounded-xl shadow-md border border-emerald-400/30 flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
              title="Descargar Reporte Estratégico Completo en PDF"
            >
              <FileDown className={`h-3.5 w-3.5 text-emerald-200 ${isGeneratingStrategicPDF ? 'animate-bounce' : ''}`} />
              <span>{isGeneratingStrategicPDF ? 'Generando...' : 'Descargar Reporte (PDF)'}</span>
            </button>
            <span className="text-[10px] text-slate-400 font-mono">
              {currentScopedClient ? `Mostrando datos exclusivos de ${currentScopedClient.name}` : 'Muestreo general'}
            </span>
          </div>
        </div>

        {filteredIncidents.length === 0 ? (
          <div className="text-center py-16 px-4 space-y-3">
            <div className="p-3 bg-[#2e302e] text-slate-400 rounded-full w-12 h-12 mx-auto flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-emerald-400" />
            </div>
            <h3 className="text-sm font-bold text-white">Sin Incidencias Registradas</h3>
            <p className="text-xs text-slate-400 max-w-sm mx-auto">
              No se encontraron folios ni reportes que coincidan con los criterios de búsqueda para {currentScopedClient?.name || 'este cliente'}.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-[#1a1b1a] border-b border-[#3a3d3a] text-slate-400 font-mono text-[10px] uppercase">
                <tr>
                  <th className="p-3.5">Folio / ID Incidencia</th>
                  <th className="p-3.5">Tipo & Origen</th>
                  <th className="p-3.5">Cliente / Centro Costos</th>
                  <th className="p-3.5">Causa / Defecto</th>
                  <th className="p-3.5 text-center">Piezas Afectadas</th>
                  <th className="p-3.5 text-center">Fotos Evidencia</th>
                  <th className="p-3.5 text-center">Estatus</th>
                  <th className="p-3.5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3a3d3a]">
                {filteredIncidents.map(inc => {
                  const isRetrabajo = inc.type === 'RETRABAJO';
                  const isInspeccion = inc.originStage === 'INSPECCION';
                  const photoCount = (inc.photos?.filter(p => !!p).length) || (inc.photoUrl ? 1 : 0);

                  return (
                    <tr key={inc.id} className="hover:bg-[#2a2c2a] transition-colors group">
                      
                      {/* Folio ID */}
                      <td className="p-3.5 font-mono font-bold">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-xs">{inc.folioId}</span>
                          <span className="text-[9px] text-slate-500 font-normal">({inc.id})</span>
                        </div>
                        <div className="text-[9.5px] text-slate-400 font-normal flex items-center gap-1 mt-0.5">
                          <Calendar className="h-3 w-3 text-slate-500" />
                          <span>{new Date(inc.dateReported).toLocaleDateString('es-MX')}</span>
                        </div>
                      </td>

                      {/* Type & Origin */}
                      <td className="p-3.5">
                        <div className="space-y-1">
                          {inc.type === 'SCRAP' || inc.originStage === 'SCRAP_ITEM_DANADO' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-950/80 text-amber-300 border border-amber-800/80">
                              <Trash2 className="h-3 w-3" />
                              SCRAP / DAÑADO
                            </span>
                          ) : isRetrabajo ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-rose-950/80 text-rose-300 border border-rose-800/80">
                              <RefreshCw className="h-3 w-3" />
                              RETRABAJO
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-red-950/80 text-red-300 border border-red-800/80">
                              <Undo2 className="h-3 w-3" />
                              RETRABAJO CLIENTE
                            </span>
                          )}

                          <div className="text-[9.5px] font-medium text-slate-300 flex items-center gap-1">
                            {inc.originStage === 'SCRAP_ITEM_DANADO' ? (
                              <span className="text-amber-300 flex items-center gap-1 font-bold">
                                ⚠️ SCRAP/ITEM DAÑADO
                              </span>
                            ) : isInspeccion ? (
                              <span className="text-purple-300 flex items-center gap-1">
                                <Eye className="h-3 w-3" /> Fase Inspección CC
                              </span>
                            ) : (
                              <span className="text-emerald-300 flex items-center gap-1">
                                <Truck className="h-3 w-3" /> Entrega a Planta
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Client / Cost Center */}
                      <td className="p-3.5">
                        <div className="font-bold text-white truncate max-w-[150px]">{inc.clientName}</div>
                        <div className="text-[10px] text-slate-400 truncate max-w-[150px]">{inc.costCenterName}</div>
                      </td>

                      {/* Cause */}
                      <td className="p-3.5 max-w-[220px]">
                        <div className="font-bold text-amber-300 truncate">{inc.causeCategory}</div>
                        <div className="text-[10px] text-slate-400 line-clamp-2 leading-tight mt-0.5">{inc.causeDetail}</div>
                      </td>

                      {/* Pieces */}
                      <td className="p-3.5 text-center font-mono">
                        <div className="font-bold text-white text-sm">{inc.affectedPieces} piezas</div>
                      </td>

                      {/* Photos Count */}
                      <td className="p-3.5 text-center font-mono">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#1a1b1a] border border-[#3a3d3a] text-xs font-bold text-slate-300">
                          <Camera className="h-3 w-3 text-cyan-400" />
                          <span>{photoCount} / 7</span>
                        </span>
                      </td>

                      {/* Status */}
                      <td className="p-3.5 text-center font-mono">
                        {inc.status === 'PENDIENTE' && (
                          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[9.5px] font-bold rounded-md">
                            ⏳ PENDIENTE
                          </span>
                        )}
                        {inc.status === 'EN_REPROCESO' && (
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 border border-blue-500/40 text-[9.5px] font-bold rounded-md">
                            🔄 EN REPROCESO
                          </span>
                        )}
                        {(inc.status === 'RESUELTO' || inc.status === 'REINSPECCIONADO') && (
                          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[9.5px] font-bold rounded-md">
                            ✅ RESUELTO
                          </span>
                        )}
                        {inc.status === 'DESECHADO' && (
                          <span className="px-2 py-0.5 bg-slate-700 text-slate-300 border border-slate-600 text-[9.5px] font-bold rounded-md">
                            🗑️ DESECHADO
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="p-3.5 text-right space-x-1.5 whitespace-nowrap">
                        {!isUserClient && (
                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(inc)}
                            title="Editar proceso completo de incidencia"
                            className="px-2.5 py-1 bg-amber-600/30 hover:bg-amber-500/50 text-amber-300 border border-amber-500/60 text-[10.5px] font-bold rounded-lg transition-all cursor-pointer inline-flex items-center gap-1 hover:scale-105 active:scale-95 shadow-sm"
                          >
                            <Pencil className="h-3 w-3" />
                            <span>Editar</span>
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => handleDownloadPDF(inc)}
                          title="Descargar Reporte PDF"
                          className="px-2 py-1 bg-red-900/40 hover:bg-red-800/60 text-red-300 border border-red-700/60 text-[10.5px] font-bold rounded-lg transition-all cursor-pointer inline-flex items-center gap-1"
                        >
                          <Download className="h-3 w-3" />
                          <span>PDF</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => setSelectedIncidentForDetail(inc)}
                          className="px-2.5 py-1 bg-[#2e302e] hover:bg-[#3e413e] text-white text-[10.5px] font-bold rounded-lg border border-[#484a48] transition-all cursor-pointer inline-flex items-center gap-1"
                        >
                          <span>Gestionar</span>
                          <ChevronRight className="h-3 w-3" />
                        </button>
                      </td>

                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODAL 1: REGISTRAR INCIDENCIA MANUAL CON 7 FOTOS */}
      <AnimatePresence>
        {isCreateModalOpen && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#242524] border border-[#3a3d3a] rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto p-6 shadow-2xl text-white space-y-5 custom-scrollbar"
            >
              <div className="flex items-center justify-between border-b border-[#3a3d3a] pb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-brand-red/20 text-brand-red rounded-xl">
                    <Plus className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-white">Registrar Reproceso / Retrabajo Cliente (Captura 7 Fotos)</h2>
                    <p className="text-xs text-slate-400">Captura un reporte técnico con hasta 7 imágenes de evidencia fotográfica.</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsCreateModalOpen(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#3a3d3a] transition-all"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSaveNewIncident} className="space-y-5">
                
                {/* Tipo de Incidencia y Origen */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1">Tipo de Registro *</label>
                    <select
                      value={formData.type}
                      onChange={e => {
                        const val = e.target.value as IncidentType;
                        setFormData({
                          ...formData,
                          type: val,
                          originStage: val === 'DEVOLUCION' ? 'ENTREGA_PLANTA' : val === 'SCRAP' ? 'SCRAP_ITEM_DANADO' : formData.originStage
                        });
                      }}
                      className="w-full bg-[#1a1b1a] border border-[#3a3d3a] text-white text-xs px-3 py-2 rounded-xl focus:border-brand-red focus:outline-none"
                    >
                      {!isUserClient && (
                        <option value="RETRABAJO">🔄 Retrabajo (Reproceso Técnico - Interno)</option>
                      )}
                      <option value="DEVOLUCION">🔄 Retrabajo Cliente</option>
                      <option value="SCRAP">🗑️ Scrap (Item Dañado / Desecho)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1">Fase de Origen *</label>
                    <select
                      value={formData.originStage}
                      onChange={e => {
                        const val = e.target.value as IncidentOrigin;
                        setFormData({
                          ...formData,
                          originStage: val,
                          type: val === 'SCRAP_ITEM_DANADO' ? 'SCRAP' : formData.type
                        });
                      }}
                      className="w-full bg-[#1a1b1a] border border-[#3a3d3a] text-white text-xs px-3 py-2 rounded-xl focus:border-brand-red focus:outline-none"
                    >
                      {!isUserClient && (
                        <option value="INSPECCION">🔍 Fase 4: Inspección Control Calidad</option>
                      )}
                      <option value="ENTREGA_PLANTA">🚚 Fase 6: Entrega a Planta Cliente</option>
                      <option value="SCRAP_ITEM_DANADO">⚠️ SCRAP/ITEM DAÑADO</option>
                    </select>
                  </div>
                </div>

                {/* Scope Restricted Fields: Client & Cost Center */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1">Folio Relacionado *</label>
                    <div className="relative">
                      <input
                        type="text"
                        list="rework-available-folios"
                        placeholder="Buscar o escribir Folio (ej. F-100234)..."
                        value={formData.folioId}
                        onChange={e => {
                          const val = e.target.value;
                          setFormData(prev => ({ ...prev, folioId: val }));
                        }}
                        className="w-full bg-[#1a1b1a] border border-[#3a3d3a] text-white text-xs px-3 py-2 rounded-xl focus:border-brand-red focus:outline-none font-mono font-bold"
                      />
                      <datalist id="rework-available-folios">
                        {folios.map(f => (
                          <option key={f.id} value={f.id}>
                            {`${f.id} - ${f.clientName || 'Cliente'} (${f.items?.length || 0} items)`}
                          </option>
                        ))}
                      </datalist>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1">Cliente *</label>
                    {currentScopedClient ? (
                      <input
                        type="text"
                        readOnly
                        value={currentScopedClient.name}
                        className="w-full bg-[#141514] border border-[#3a3d3a] text-brand-red font-bold text-xs px-3 py-2 rounded-xl cursor-not-allowed"
                      />
                    ) : (
                      <select
                        value={formData.clientId}
                        onChange={e => {
                          const cId = e.target.value;
                          const ccList = costCenters.filter(cc => cc.clientId === cId);
                          setFormData({
                            ...formData,
                            clientId: cId,
                            costCenterId: ccList[0]?.id || costCenters[0]?.id || ''
                          });
                        }}
                        className="w-full bg-[#1a1b1a] border border-[#3a3d3a] text-white text-xs px-3 py-2 rounded-xl focus:border-brand-red focus:outline-none font-semibold"
                      >
                        {clients.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1">Centro de Costos / Ubicación *</label>
                    <select
                      value={formData.costCenterId}
                      onChange={e => setFormData({ ...formData, costCenterId: e.target.value })}
                      className="w-full bg-[#1a1b1a] border border-[#3a3d3a] text-white text-xs px-3 py-2 rounded-xl focus:border-brand-red focus:outline-none font-semibold"
                    >
                      {availableCostCenters.map(cc => (
                        <option key={cc.id} value={cc.id}>{cc.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* 📦 DETALLE DE ITEMS / MERCANCÍAS DEL FOLIO ENCONTRADO */}
                {matchedFolio ? (() => {
                  const totalFolioPieces = matchedFolio.items?.reduce((a, b) => a + (Number(b.quantity) || 0), 0) || 1;
                  const allItemsSummary = matchedFolio.items?.map(i => `${i.description} (${i.quantity} pzas)`).join(', ') || 'Todo el lote registrado';

                  return (
                    <div className="bg-[#1a1b1a] border border-[#3a3d3a] rounded-2xl p-4 space-y-3 shadow-inner">
                      <div className="flex items-center justify-between border-b border-[#3a3d3a] pb-2.5">
                        <div className="flex items-center gap-2">
                          <Receipt className="h-4 w-4 text-brand-red" />
                          <span className="font-mono font-black text-sm text-white">
                            Detalle del Folio: #{matchedFolio.id}
                          </span>
                        </div>
                        <span className="bg-brand-red/20 text-red-300 border border-brand-red/40 px-2.5 py-0.5 rounded-md text-[10.5px] font-bold font-mono">
                          Total Folio: {totalFolioPieces} piezas
                        </span>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-[10px] text-slate-400 font-bold block uppercase">Cliente:</span>
                          <span className="font-bold text-white">{matchedFolio.clientName || 'Cliente'}</span>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-400 font-bold block uppercase">Destino:</span>
                          <span className="font-bold text-white">{matchedFolio.destinationCity || 'San Luis Potosí'}</span>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-400 font-bold block uppercase">Estatus Actual:</span>
                          <span className="font-extrabold text-amber-400 font-mono">{matchedFolio.status}</span>
                        </div>
                      </div>

                      {/* Lista de Mercancías del Folio */}
                      <div className="space-y-1.5 pt-1">
                        <div className="flex items-center justify-between text-[11px] font-bold text-slate-300">
                          <span className="uppercase text-[10px] tracking-wider text-slate-400">
                            Mercancías Registradas ({matchedFolio.items?.length || 0}):
                          </span>
                          <span className="text-[10px] text-slate-400 italic">
                            Haz clic en un item para seleccionarlo individualmente
                          </span>
                        </div>

                        <div className="bg-[#141514] border border-[#3a3d3a] rounded-xl overflow-hidden divide-y divide-[#2a2c2a] max-h-36 overflow-y-auto custom-scrollbar">
                          {matchedFolio.items && matchedFolio.items.length > 0 ? (
                            matchedFolio.items.map((item, idx) => (
                              <div
                                key={idx}
                                onClick={() => {
                                  setReworkScope('PARCIAL');
                                  setFormData(prev => ({
                                    ...prev,
                                    reworkPiecesName: item.description,
                                    affectedPieces: Number(item.quantity) || 1
                                  }));
                                }}
                                className="p-2 flex items-center justify-between text-xs hover:bg-[#252725] transition-colors cursor-pointer group"
                              >
                                <div className="flex items-center gap-2 pr-2">
                                  <Package className="h-3.5 w-3.5 text-cyan-400 shrink-0 group-hover:scale-110 transition-transform" />
                                  <span className="font-semibold text-slate-200 group-hover:text-cyan-300 transition-colors">
                                    {item.description}
                                  </span>
                                </div>
                                <span className="font-mono font-bold text-cyan-300 bg-[#222422] px-2 py-0.5 rounded border border-[#3a3d3a] text-[11px] shrink-0">
                                  {item.quantity} {item.unit || 'pzas'}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="p-2.5 text-slate-400 italic text-xs text-center">
                              1 Lote General de Mercancía ({totalFolioPieces} pzas)
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Scope Selectors: Retrabajo Cliente/Interno Total vs Parcial */}
                      <div className="pt-2 border-t border-[#3a3d3a] space-y-2">
                        <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-200 block">
                          ¿Qué cantidad se enviará a {formData.type === 'DEVOLUCION' ? 'Retrabajo Cliente' : 'Retrabajo Interno'}? *
                        </label>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setReworkScope('TOTAL');
                              setFormData(prev => ({
                                ...prev,
                                affectedPieces: totalFolioPieces,
                                reworkPiecesName: `Todo el Folio #${matchedFolio.id} (${allItemsSummary})`
                              }));
                            }}
                            className={`p-3 rounded-xl border text-left flex items-start gap-2.5 transition-all cursor-pointer ${
                              reworkScope === 'TOTAL'
                                ? 'bg-red-950/70 border-brand-red text-white ring-1 ring-brand-red shadow-md'
                                : 'bg-[#141514] border-[#3a3d3a] text-slate-400 hover:border-slate-500'
                            }`}
                          >
                            <input
                              type="radio"
                              name="reworkScopeManager"
                              checked={reworkScope === 'TOTAL'}
                              onChange={() => {}}
                              className="mt-0.5 text-brand-red focus:ring-brand-red cursor-pointer"
                            />
                            <div>
                              <div className="font-black text-xs text-white">
                                {formData.type === 'DEVOLUCION' ? 'Retrabajo Cliente' : 'Retrabajar'} TODO el Folio Completo
                              </div>
                              <div className="text-[10px] text-slate-400 mt-0.5 font-medium">
                                Se procesan las <strong className="text-cyan-400 font-mono">{totalFolioPieces} piezas</strong> totales del folio.
                              </div>
                            </div>
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setReworkScope('PARCIAL');
                              if (formData.affectedPieces >= totalFolioPieces) {
                                setFormData(prev => ({ ...prev, affectedPieces: Math.max(1, totalFolioPieces - 1) }));
                              }
                              if (formData.reworkPiecesName.startsWith('Todo el Folio')) {
                                setFormData(prev => ({ ...prev, reworkPiecesName: '' }));
                              }
                            }}
                            className={`p-3 rounded-xl border text-left flex items-start gap-2.5 transition-all cursor-pointer ${
                              reworkScope === 'PARCIAL'
                                ? 'bg-red-950/70 border-brand-red text-white ring-1 ring-brand-red shadow-md'
                                : 'bg-[#141514] border-[#3a3d3a] text-slate-400 hover:border-slate-500'
                            }`}
                          >
                            <input
                              type="radio"
                              name="reworkScopeManager"
                              checked={reworkScope === 'PARCIAL'}
                              onChange={() => {}}
                              className="mt-0.5 text-brand-red focus:ring-brand-red cursor-pointer"
                            />
                            <div>
                              <div className="font-black text-xs text-white">
                                {formData.type === 'DEVOLUCION' ? 'Retrabajo Cliente' : 'Retrabajo'} Parcial / Por Cantidad
                              </div>
                              <div className="text-[10px] text-slate-400 mt-0.5 font-medium">
                                Especifique solo la cantidad de piezas o componentes con falla.
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })() : formData.folioId.trim() ? (
                  <div className="bg-[#1a1b1a] border border-amber-800/60 rounded-xl p-3 text-xs text-amber-300 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
                      <span>
                        Folio <strong>#{formData.folioId}</strong> no se encuentra registrado en el sistema. Se procesará como captura manual.
                      </span>
                    </div>
                  </div>
                ) : null}

                {/* Defect details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1">Categoría de Causa *</label>
                    <select
                      value={formData.causeCategory}
                      onChange={e => setFormData({ ...formData, causeCategory: e.target.value })}
                      className="w-full bg-[#1a1b1a] border border-[#3a3d3a] text-white text-xs px-3 py-2 rounded-xl focus:border-brand-red focus:outline-none"
                    >
                      <option value="MATERIAL DAÑADO">⚠️ MATERIAL DAÑADO</option>
                      <option value="Mala Adherencia de Pintura">Mala Adherencia de Pintura</option>
                      <option value="Decapado Incompleto">Decapado Incompleto / Residuos</option>
                      <option value="Oxidación Severa">Oxidación Severa / Corrosión</option>
                      <option value="Golpe en Transporte">Golpe o Daño en Transporte</option>
                      <option value="Especificación Incorrecta">Especificación Fuera de Micraje</option>
                      <option value="Porosidad / Ampollamiento">Porosidad / Ampollamiento</option>
                      <option value="Rechazo Cliente">Rechazo Directo por Cliente</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1">Departamento Responsable</label>
                    <select
                      value={formData.responsibleDept}
                      onChange={e => setFormData({ ...formData, responsibleDept: e.target.value as ResponsibleDept })}
                      className="w-full bg-[#1a1b1a] border border-[#3a3d3a] text-white text-xs px-3 py-2 rounded-xl focus:border-brand-red focus:outline-none"
                    >
                      <option value="CALIDAD">Control de Calidad</option>
                      <option value="DECAPADO_PINTURA">Decapado y Pintura</option>
                      <option value="LOGISTICA_TRANSPORTE">Logística y Transporte</option>
                      <option value="PRODUCCION">Producción Taller</option>
                      <option value="CLIENTE">Atribuible al Cliente</option>
                    </select>
                  </div>
                </div>

                {/* Impact Metrics */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold text-slate-300 mb-1">Piezas / Componentes a Retrabajar *</label>
                    <input
                      type="text"
                      placeholder="Ej. Rejillas de suspensión, ganchos laterales, etc."
                      value={formData.reworkPiecesName}
                      onChange={e => setFormData({ ...formData, reworkPiecesName: e.target.value })}
                      className="w-full bg-[#1a1b1a] border border-[#3a3d3a] text-white text-xs px-3 py-2 rounded-xl focus:border-brand-red focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1">Cantidad a Retrabajar *</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.affectedPieces}
                      onChange={e => setFormData({ ...formData, affectedPieces: Number(e.target.value) })}
                      className="w-full bg-[#1a1b1a] border border-[#3a3d3a] text-white text-xs px-3 py-2 rounded-xl focus:border-brand-red focus:outline-none font-bold text-red-400"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1">Prioridad</label>
                    <select
                      value={formData.priority}
                      onChange={e => setFormData({ ...formData, priority: e.target.value as IncidentPriority })}
                      className="w-full bg-[#1a1b1a] border border-[#3a3d3a] text-white text-xs px-3 py-2 rounded-xl focus:border-brand-red focus:outline-none font-bold"
                    >
                      <option value="BAJA">🟢 BAJA</option>
                      <option value="MEDIA">🔵 MEDIA</option>
                      <option value="ALTA">🟡 ALTA</option>
                      <option value="CRITICA">🔥 CRÍTICA</option>
                    </select>
                  </div>
                </div>

                {/* Cause Detail & Action Plan */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1">Detalle del Defecto / Incidencia *</label>
                    <textarea
                      rows={2}
                      placeholder="Describe la falla técnica, micraje o daño visible..."
                      value={formData.causeDetail}
                      onChange={e => setFormData({ ...formData, causeDetail: e.target.value })}
                      className="w-full bg-[#1a1b1a] border border-[#3a3d3a] text-white text-xs p-3 rounded-xl focus:border-brand-red focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1">Plan de Acción / Corrección</label>
                    <textarea
                      rows={2}
                      placeholder="Acción correctiva propuesta para el reingreso o reparación..."
                      value={formData.actionPlan}
                      onChange={e => setFormData({ ...formData, actionPlan: e.target.value })}
                      className="w-full bg-[#1a1b1a] border border-[#3a3d3a] text-white text-xs p-3 rounded-xl focus:border-brand-red focus:outline-none"
                    />
                  </div>
                </div>

                {/* 📸 7 IMAGES CAPTURE / UPLOAD SECTION */}
                <div className="space-y-2 border-t border-[#3a3d3a] pt-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-white uppercase tracking-wider font-mono flex items-center gap-1.5">
                      <Camera className="h-4 w-4 text-cyan-400" />
                      <span>Evidencia Fotográfica de la Incidencia (Hasta 7 Imágenes)</span>
                    </label>
                    <span className="text-[10px] text-slate-400 font-mono">
                      {formData.photos.filter(p => !!p).length} / 7 Fotos cargadas
                    </span>
                  </div>

                  {/* 7 Slots Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {PHOTO_SLOT_LABELS.map((slotLabel, idx) => {
                      const photoUrl = formData.photos[idx];

                      return (
                        <div key={idx} className="bg-[#1a1b1a] border border-[#3a3d3a] rounded-xl p-2 flex flex-col justify-between space-y-2 relative group">
                          
                          {/* Slot Label */}
                          <div className="text-[9.5px] font-bold text-slate-300 truncate font-mono">
                            {slotLabel}
                          </div>

                          {/* Image Box */}
                          <div className="h-28 bg-[#141514] border border-dashed border-[#3a3d3a] rounded-lg overflow-hidden flex items-center justify-center relative">
                            {photoUrl ? (
                              <>
                                <img src={photoUrl} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                                <button
                                  type="button"
                                  onClick={() => handleRemovePhoto(idx)}
                                  className="absolute top-1 right-1 p-1 bg-red-600/90 text-white rounded-full hover:bg-red-700 transition-all cursor-pointer shadow-md"
                                  title="Eliminar foto"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </>
                            ) : (
                              <div
                                onClick={() => fileInputRefs.current[idx]?.click()}
                                className="flex flex-col items-center justify-center space-y-1 text-slate-500 hover:text-cyan-400 cursor-pointer p-2 text-center"
                              >
                                <Upload className="h-5 w-5" />
                                <span className="text-[9px] font-bold">Subir / Capturar</span>
                              </div>
                            )}

                            {/* Hidden file input */}
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              ref={el => { fileInputRefs.current[idx] = el; }}
                              onChange={e => handlePhotoUpload(idx, e)}
                              className="hidden"
                            />
                          </div>

                          {/* Action Button */}
                          {!photoUrl && (
                            <button
                              type="button"
                              onClick={() => fileInputRefs.current[idx]?.click()}
                              className="w-full py-1 bg-[#2a2c2a] hover:bg-[#383a38] text-slate-300 text-[10px] font-bold rounded-lg border border-[#3a3d3a] transition-all cursor-pointer flex items-center justify-center gap-1"
                            >
                              <Camera className="h-3 w-3 text-cyan-400" />
                              <span>Foto {idx + 1}</span>
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Form Buttons */}
                <div className="flex items-center justify-end gap-2 border-t border-[#3a3d3a] pt-4">
                  <button
                    type="button"
                    onClick={() => setIsCreateModalOpen(false)}
                    className="px-4 py-2 bg-[#2a2c2a] hover:bg-[#3a3d3a] text-slate-300 text-xs font-bold rounded-xl transition-all cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-brand-red hover:bg-red-700 text-white text-xs font-bold rounded-xl shadow-lg transition-all cursor-pointer flex items-center gap-2"
                  >
                    <Check className="h-4 w-4" />
                    <span>Guardar Incidencia Manual</span>
                  </button>
                </div>

              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL: HISTORIAL DE RETRABAJOS Y DEVOLUCIONES POR FOLIO */}
      <AnimatePresence>
        {isHistoryModalOpen && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#141514] border border-[#3a3d3a] rounded-3xl max-w-5xl w-full p-6 space-y-5 shadow-2xl max-h-[90vh] flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between border-b border-[#2a2d2a] pb-4 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-amber-500/20 to-red-500/20 border border-amber-500/30 text-amber-400 rounded-2xl">
                    <FolderClock className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-black text-white flex items-center gap-2 tracking-tight">
                      <span>Historial de Retrabajos</span>
                      <span className="text-xs bg-amber-500/20 text-amber-300 px-2.5 py-0.5 rounded-full border border-amber-500/30 font-mono font-bold">
                        {combinedIncidents.length} Registros
                      </span>
                    </h2>
                    <p className="text-xs text-slate-400">
                      Reportes consolidados de retrabajos e incidencias por folio con evidencia fotográfica de hasta 7 fotos y generación de PDF.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleDownloadStrategicReportPDF}
                    disabled={isGeneratingStrategicPDF}
                    className="px-3.5 py-1.5 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white text-xs font-bold rounded-xl shadow-md border border-emerald-400/30 flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
                    title="Descargar Reporte Estratégico Completo en PDF"
                  >
                    <FileDown className={`h-4 w-4 text-emerald-200 ${isGeneratingStrategicPDF ? 'animate-bounce' : ''}`} />
                    <span>{isGeneratingStrategicPDF ? 'Generando...' : 'Descargar Reporte (PDF)'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsHistoryModalOpen(false)}
                    className="text-slate-400 hover:text-white p-2 rounded-xl hover:bg-[#222422] transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Search & Filter Controls */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 shrink-0 bg-[#1c1d1c] p-3 rounded-2xl border border-[#2d302d]">
                {/* Search Bar */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Buscar por Folio (#F-...), cliente, piezas o descripción..."
                    value={historySearchQuery}
                    onChange={(e) => setHistorySearchQuery(e.target.value)}
                    className="w-full bg-[#141514] border border-[#3a3d3a] text-white text-xs pl-9 pr-3 py-2 rounded-xl focus:border-amber-500 focus:outline-none placeholder-slate-500 font-medium"
                  />
                </div>

                {/* Filter Tabs */}
                <div className="flex items-center gap-1.5 bg-[#141514] p-1 rounded-xl border border-[#3a3d3a] shrink-0">
                  <button
                    type="button"
                    onClick={() => setHistoryFilterType('ALL')}
                    className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all cursor-pointer ${
                      historyFilterType === 'ALL'
                        ? 'bg-amber-500 text-black shadow-md'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    {isUserClient ? 'Todos los Retrabajos' : 'Todos'} ({combinedIncidents.length})
                  </button>
                  {!isUserClient && (
                    <button
                      type="button"
                      onClick={() => setHistoryFilterType('RETRABAJO')}
                      className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all cursor-pointer ${
                        historyFilterType === 'RETRABAJO'
                          ? 'bg-red-500 text-white shadow-md'
                          : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      🔄 Retrabajos Internos ({combinedIncidents.filter(i => i.type === 'RETRABAJO').length})
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setHistoryFilterType('DEVOLUCION')}
                    className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all cursor-pointer ${
                      historyFilterType === 'DEVOLUCION'
                        ? 'bg-purple-500 text-white shadow-md'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    🔄 Retrabajos Cliente ({combinedIncidents.filter(i => i.type === 'DEVOLUCION').length})
                  </button>
                </div>
              </div>

              {/* Scrollable List of Folio History Items */}
              <div className="flex-1 overflow-y-auto space-y-4 pr-1 custom-scrollbar">
                {(() => {
                  const filteredList = combinedIncidents.filter(inc => {
                    if (historyFilterType === 'RETRABAJO' && inc.type !== 'RETRABAJO') return false;
                    if (historyFilterType === 'DEVOLUCION' && inc.type !== 'DEVOLUCION') return false;

                    if (historySearchQuery.trim()) {
                      const q = historySearchQuery.toLowerCase();
                      return (
                        inc.folioId.toLowerCase().includes(q) ||
                        inc.id.toLowerCase().includes(q) ||
                        inc.clientName.toLowerCase().includes(q) ||
                        inc.causeCategory.toLowerCase().includes(q) ||
                        inc.causeDetail.toLowerCase().includes(q) ||
                        (inc.reworkPiecesName && inc.reworkPiecesName.toLowerCase().includes(q))
                      );
                    }
                    return true;
                  });

                  if (filteredList.length === 0) {
                    return (
                      <div className="text-center py-16 bg-[#181918] rounded-2xl border border-dashed border-[#2e312e] space-y-2">
                        <FolderClock className="h-10 w-10 text-slate-500 mx-auto opacity-50" />
                        <p className="text-sm font-bold text-slate-300">No se encontraron reportes en el historial</p>
                        <p className="text-xs text-slate-500">Prueba cambiando el filtro o término de búsqueda.</p>
                      </div>
                    );
                  }

                  return filteredList.map((inc) => {
                    const photosCount = inc.photos?.filter(p => !!p).length || (inc.photoUrl ? 1 : 0);

                    return (
                      <div
                        key={inc.id}
                        className="bg-[#181918] border border-[#2d302d] hover:border-amber-500/40 rounded-2xl p-4 space-y-3 transition-all hover:shadow-xl"
                      >
                        {/* Record Top Bar */}
                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#282a28] pb-3">
                          <div className="flex items-center gap-2.5">
                            <span className="font-mono text-xs font-black text-amber-400 bg-amber-500/10 px-2.5 py-1 rounded-lg border border-amber-500/20">
                              Folio: #{inc.folioId}
                            </span>
                            <span className="text-xs font-bold text-white">{inc.clientName}</span>
                            <span className="text-[11px] text-slate-400">({inc.costCenterName})</span>
                          </div>

                          <div className="flex items-center gap-2">
                            <span
                              className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full uppercase font-mono ${
                                inc.type === 'DEVOLUCION'
                                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                                  : 'bg-red-500/20 text-red-300 border border-red-500/30'
                              }`}
                            >
                              {inc.type === 'DEVOLUCION' ? '🔄 Retrabajo Cliente' : inc.type === 'SCRAP' ? '⚠️ Scrap / Dañado' : '🔄 Retrabajo Interno'}
                            </span>

                            <span className="text-[10px] text-slate-400 font-mono">
                              {new Date(inc.dateReported).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        </div>

                        {/* Record Main Details */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                          {/* Piezas a Retrabajar */}
                          <div className="bg-[#111211] p-3 rounded-xl border border-[#252725] space-y-1">
                            <span className="text-[10px] font-extrabold text-amber-400 uppercase tracking-wider block font-mono">
                              Piezas a Retrabajar / Afectadas
                            </span>
                            <p className="font-bold text-slate-200">
                              {inc.reworkPiecesName || 'Piezas y bastidores de la orden'}
                            </p>
                            <p className="text-[11px] font-extrabold text-red-400 font-mono">
                              Cantidad: {inc.affectedPieces} pzas
                            </p>
                          </div>

                          {/* Causa Raíz */}
                          <div className="bg-[#111211] p-3 rounded-xl border border-[#252725] space-y-1 md:col-span-2">
                            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block font-mono">
                              Categoría y Detalle del Defecto
                            </span>
                            <p className="font-bold text-white">{inc.causeCategory}</p>
                            <p className="text-slate-300 line-clamp-2 text-[11px]">{inc.causeDetail}</p>
                          </div>
                        </div>

                        {/* Photos Attached Section (Up to 7 Photos) */}
                        <div className="space-y-1.5 pt-1">
                          <div className="flex items-center justify-between text-[11px]">
                            <span className="font-mono font-bold text-slate-300 flex items-center gap-1.5">
                              <Camera className="h-3.5 w-3.5 text-cyan-400" />
                              <span>Evidencia Fotográfica de la Incidencia:</span>
                            </span>
                            <span className="text-slate-400 font-mono text-[10px]">
                              {photosCount} / 7 fotos anexadas
                            </span>
                          </div>

                          <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                            {(inc.photos && inc.photos.length > 0) ? (
                              inc.photos.filter(p => !!p).map((pUrl, pIdx) => (
                                <div key={pIdx} className="h-16 bg-black border border-[#333633] rounded-lg overflow-hidden relative group">
                                  <img src={pUrl} alt={`Evidencia ${pIdx + 1}`} className="w-full h-full object-cover" />
                                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <span className="text-[8px] font-mono text-white font-bold bg-black/70 px-1 py-0.5 rounded">
                                      Foto {pIdx + 1}
                                    </span>
                                  </div>
                                </div>
                              ))
                            ) : inc.photoUrl ? (
                              <div className="h-16 bg-black border border-[#333633] rounded-lg overflow-hidden relative">
                                <img src={inc.photoUrl} alt="Foto 1" className="w-full h-full object-cover" />
                              </div>
                            ) : (
                              <div className="col-span-full py-2 text-center text-[10px] text-slate-500 bg-[#111211] rounded-lg border border-[#222422]">
                                Sin fotos adjuntas
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Bottom Actions Bar */}
                        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[#252725] pt-3">
                          <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
                            <User className="h-3.5 w-3.5 text-slate-500" />
                            <span>Reportado por: <strong className="text-slate-200">{inc.reportedBy}</strong></span>
                          </div>

                          <div className="flex items-center gap-2">
                            {!isUserClient && (
                              <button
                                type="button"
                                onClick={() => {
                                  handleOpenEditModal(inc);
                                  setIsHistoryModalOpen(false);
                                }}
                                className="bg-amber-600/30 hover:bg-amber-600/60 text-amber-200 hover:text-white font-bold px-3.5 py-2 rounded-xl text-xs border border-amber-500/50 flex items-center gap-1.5 transition-all cursor-pointer hover:scale-105 active:scale-95 shadow-md"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                <span>Editar Incidencia</span>
                              </button>
                            )}

                            {/* Aesthetic PDF Report Button */}
                            <button
                              type="button"
                              onClick={() => handleDownloadPDF(inc)}
                              className="bg-gradient-to-r from-red-600 via-brand-red to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-black px-4 py-2 rounded-xl text-xs shadow-lg shadow-red-950/50 border border-red-400/30 flex items-center gap-2 transition-all cursor-pointer hover:scale-105 active:scale-95"
                            >
                              <FileText className="h-4 w-4 text-red-100" />
                              <span>Descargar Reporte </span>
                              <Download className="h-3.5 w-3.5 text-red-200 ml-1" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-between border-t border-[#2a2d2a] pt-3 text-xs text-slate-500 shrink-0 font-mono">
                <span>Sistema de Trazabilidad y Control</span>
                <button
                  type="button"
                  onClick={() => setIsHistoryModalOpen(false)}
                  className="px-4 py-1.5 bg-[#222422] hover:bg-[#2c2f2c] text-slate-300 font-bold rounded-xl"
                >
                  Cerrar Historial
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL 2: GESTIONAR DETAIL & RESOLUTION + PDF GENERATOR */}
      <AnimatePresence>
        {selectedIncidentForDetail && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#242524] border border-[#3a3d3a] rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 shadow-2xl text-white space-y-5 custom-scrollbar"
            >
              {/* Header */}
              <div className="flex items-center justify-between border-b border-[#3a3d3a] pb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-rose-500/20 text-rose-400 rounded-xl">
                    <ShieldAlert className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-black text-white">Incidencia {selectedIncidentForDetail.id}</h2>
                      <span className="text-xs font-mono font-bold text-amber-300">({selectedIncidentForDetail.folioId})</span>
                    </div>
                    <p className="text-xs text-slate-400">{selectedIncidentForDetail.clientName} • {selectedIncidentForDetail.costCenterName}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!isUserClient && (
                    <button
                      type="button"
                      onClick={() => {
                        handleOpenEditModal(selectedIncidentForDetail);
                        setSelectedIncidentForDetail(null);
                      }}
                      className="px-3 py-1.5 bg-amber-600/30 hover:bg-amber-600/60 text-amber-200 border border-amber-500/50 text-xs font-bold rounded-xl shadow-lg transition-all cursor-pointer flex items-center gap-1.5 hover:scale-105 active:scale-95"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      <span>Editar Incidencia</span>
                    </button>
                  )}

                  <button
                    onClick={() => handleDownloadPDF(selectedIncidentForDetail)}
                    className="px-3 py-1.5 bg-red-900/60 hover:bg-red-800 text-red-200 border border-red-700 text-xs font-bold rounded-xl shadow-lg transition-all cursor-pointer flex items-center gap-1.5"
                  >
                    <Download className="h-4 w-4" />
                    <span>Descargar PDF</span>
                  </button>

                  <button
                    onClick={() => setSelectedIncidentForDetail(null)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#3a3d3a] transition-all"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Status Banner */}
              <div className="bg-[#1a1b1a] border border-[#3a3d3a] rounded-xl p-3 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="space-y-1">
                  <div className="text-[10px] font-bold text-slate-400 uppercase font-mono">
                    {selectedIncidentForDetail.type === 'DEVOLUCION' ? 'Estatus del Retrabajo Cliente' : 'Estatus del Reproceso'}
                  </div>
                  <div className="text-sm font-black text-white flex items-center gap-2">
                    {selectedIncidentForDetail.status === 'PENDIENTE' && <span className="text-amber-400">⏳ PENDIENTE DE ATENCIÓN</span>}
                    {selectedIncidentForDetail.status === 'EN_REPROCESO' && <span className="text-blue-400">🔄 EN PROCESO DE ATENCIÓN</span>}
                    {(selectedIncidentForDetail.status === 'RESUELTO' || selectedIncidentForDetail.status === 'REINSPECCIONADO') && <span className="text-emerald-400">✅ RESUELTO Y REINSPECCIONADO</span>}
                    {selectedIncidentForDetail.status === 'DESECHADO' && <span className="text-slate-400">🗑️ DESECHADO / NO RECUPERABLE</span>}
                  </div>
                </div>

                {!isUserClient && (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => handleUpdateIncidentStatus(selectedIncidentForDetail.id, 'EN_REPROCESO')}
                      className="px-3 py-1.5 bg-blue-900/50 hover:bg-blue-800 text-blue-200 border border-blue-700 text-xs font-bold rounded-xl transition-all cursor-pointer"
                    >
                      En Reproceso
                    </button>
                    <button
                      onClick={() => handleUpdateIncidentStatus(selectedIncidentForDetail.id, 'RESUELTO')}
                      className="px-3 py-1.5 bg-emerald-900/50 hover:bg-emerald-800 text-emerald-200 border border-emerald-700 text-xs font-bold rounded-xl transition-all cursor-pointer"
                    >
                      Marcar Resuelto
                    </button>
                  </div>
                )}
              </div>

              {/* Defect Info Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs bg-[#1a1b1a] p-4 rounded-xl border border-[#3a3d3a]">
                <div>
                  <span className="text-slate-400 font-bold block">Categoría:</span>
                  <span className="text-amber-300 font-bold text-sm">{selectedIncidentForDetail.causeCategory}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-bold block">Fase de Origen:</span>
                  <span className="text-purple-300 font-bold">
                    {selectedIncidentForDetail.originStage === 'SCRAP_ITEM_DANADO'
                      ? '⚠️ SCRAP/ITEM DAÑADO'
                      : selectedIncidentForDetail.originStage === 'INSPECCION'
                      ? 'Fase 4: Inspección CC'
                      : 'Fase 6: Entrega a Planta'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 font-bold block">Piezas Afectadas:</span>
                  <span className="text-white font-bold">{selectedIncidentForDetail.affectedPieces} pzas</span>
                </div>
                <div>
                  <span className="text-slate-400 font-bold block">Reportado Por:</span>
                  <span className="text-slate-200">{selectedIncidentForDetail.reportedBy}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-bold block">Fecha:</span>
                  <span className="text-slate-200">{new Date(selectedIncidentForDetail.dateReported).toLocaleString('es-MX')}</span>
                </div>
                <div className="col-span-full">
                  <span className="text-slate-400 font-bold block">Detalle de la Falla:</span>
                  <p className="text-slate-200 mt-1 bg-[#141514] p-2.5 rounded-lg border border-[#2e302e]">
                    {selectedIncidentForDetail.causeDetail}
                  </p>
                </div>
              </div>

              {/* Photos Gallery */}
              <div className="space-y-2">
                <h3 className="text-xs font-bold text-white uppercase tracking-wider font-mono flex items-center gap-1.5">
                  <Camera className="h-4 w-4 text-cyan-400" />
                  <span>Evidencia Fotográfica ({selectedIncidentForDetail.photos?.filter(p => !!p).length || (selectedIncidentForDetail.photoUrl ? 1 : 0)} imágenes)</span>
                </h3>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(selectedIncidentForDetail.photos && selectedIncidentForDetail.photos.length > 0) ? (
                    selectedIncidentForDetail.photos.filter(p => !!p).map((pUrl, idx) => (
                      <div key={idx} className="h-28 bg-[#1a1b1a] border border-[#3a3d3a] rounded-lg overflow-hidden group relative">
                        <img src={pUrl} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                        <div className="absolute bottom-0 inset-x-0 bg-black/70 text-[9px] text-white p-1 font-mono text-center">
                          Foto {idx + 1}
                        </div>
                      </div>
                    ))
                  ) : selectedIncidentForDetail.photoUrl ? (
                    <div className="h-28 bg-[#1a1b1a] border border-[#3a3d3a] rounded-lg overflow-hidden">
                      <img src={selectedIncidentForDetail.photoUrl} alt="Foto 1" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="col-span-full text-center py-6 text-xs text-slate-500 bg-[#1a1b1a] rounded-xl border border-[#3a3d3a]">
                      Sin fotografías registradas
                    </div>
                  )}
                </div>
              </div>

              {/* Action Plan / Notes */}
              {selectedIncidentForDetail.actionPlan && (
                <div className="space-y-1">
                  <span className="text-xs font-bold text-slate-300">Plan de Acción / Instrucciones Técnicas:</span>
                  <div className="bg-[#1a1b1a] p-3 rounded-xl border border-[#3a3d3a] text-xs text-emerald-300">
                    {selectedIncidentForDetail.actionPlan}
                  </div>
                </div>
              )}

            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL 3: EDITAR PROCESO COMPLETO DE INCIDENCIA */}
      <EditReworkIncidentModal
        isOpen={Boolean(editingIncident)}
        editFormData={editFormData}
        setEditFormData={setEditFormData}
        onClose={() => setEditingIncident(null)}
        onSave={handleSaveEditIncident}
        clients={clients}
        costCenters={costCenters}
        handleEditPhotoUpload={handleEditPhotoUpload}
        handleEditRemovePhoto={handleEditRemovePhoto}
        editFileInputRefs={editFileInputRefs}
      />

    </div>
  );
}
